import { Middleware, AnyAction } from '@reduxjs/toolkit';
import { MiddlewareActionMeta, SpeakerPayloadMeta } from 'types/middleware/actionMeta';
import { MIC_CATEGORIES } from 'types/models/microphone';
import { getIsLoadingDesign } from './loadingFlag';
import toolIndex from "toolConstants";
import ReducersIndex from 'reduxModules/ducks';
import { TABLE_MIC_Z_HEIGHT } from 'reduxModules/ducks/room';
import { getDeviceLayouts, getCoverageBoxes, setTileRelocationAllowed, isAnyDeviceCustomized } from "reduxModules/calculations/roomCalculations";
import { getFilteredDeviceLayout, buildEquipmentState } from "reduxModules/calculations/layoutFilter";
import { isEmpty, round } from "lodash";
import { getBoundingBox, getIsMeter, getScale } from "reduxModules/ducks/ui";
import { getEquipmentSummary } from "reduxModules/selectors/containerSelectors";
import { showWarning, showInfo, showError, showSuccess } from "utilities/toastManager";
import validate from "toolConstants/conferenceroomDesigner/validation";
import { calculateBarCoverageGap, calculateBarSpeakerCoverageGap, calcExpansionTableMicPlacement, calcExpansionCeilingMicPlacement, getExpansionCeilingIsFocused, MIN_MIC_SPKR_DISTANCE_M } from "toolConstants/conferenceroomDesigner/logic";
import { checkPortOverflow, maxExpansionSpeakerCountFor } from "toolConstants/conferenceroomDesigner/treeFrogPorts";
import MuiTableConstants from "config/tables/tableConstants";
import { side } from "utilities/mathematic";
import { distanceToString } from "utilities/format";
interface Store {
  getState: () => any;
  dispatch: (action: any) => void;
}

/**
 * @module calculateDevicesMiddleware
 * @description Synchronous Redux middleware for managing device calculations and layouts.
 * This middleware provides predictable, synchronous state updates for device calculations.
 * 
 * Key features:
 * - No setTimeout or async operations
 * - Predictable execution order
 * - No race conditions
 * - Easier to test and debug
 * - Better performance (no unnecessary delays)
 */

/**
 * Actions that should trigger device recalculation
 */
const RECALCULATION_TRIGGERS = [
  'container/setAcoustics',         // Acoustics quality affects ceiling mic height (maxHeight/height recalc)
  'container/setDimensions',
  'container/setCeilingHeight',
  'container/setTable',
  'container/setCeilingTiles',
  'container/setMicrophones',          // Mic height/coverage change → BMA360D amp recalc (PIG-480/WDT-78)
  'container/setSpeakers',             // Speaker count change → BMA360D amp escalation recalc (PIG-480/WDT-78)
  'container/setSpeakerMountingType',  // Added: Speaker mounting type changes trigger recalc (wall ↔ ceiling flips BMA360D amp config — PIG-479/480)
  'container/setSpeakerModel',          // Added: Speaker model changes trigger recalc
  'container/toggleSpeakerLink',        // Added: Speaker link toggle triggers recalc and toast notification
  'container/setCameras',
  'container/addDevice',
  'container/removeDevice',
  'container/moveDevice',
  'container/updateArea',
  'container/resetArea',        // Added: Undo button triggers this
  'container/setRecalculate',   // Added: Force recalculation flag
  'container/setCustomized',    // Added: Customization changes
  'container/updateDevice',     // Added: Undo button dispatches this to clear device state
  'container/setProductFamily',  // Added: Product family changes should recalculate equipment
  'container/setUCCompute',     // Added: UC Compute changes should recalculate equipment
  'container/setExpansionMicType',          // Expansion mic type selection triggers layout recalc
  'container/setExpansionMicCoverage',      // Expansion mic coverage change triggers layout recalc
  'container/setExpansionMicrophones',      // Coverage Density fader (manual-add table flow) — value change triggers placement recalc
  'container/addExpansionMic',              // Manual + click adds a mic — calc must re-run so the equipment list + port-overflow toast pick up the new chain count
  'container/removeExpansionMic',           // Manual − click removes a mic — same reason as above
  'container/placeExpansionMicAtLocation',  // Click-to-place atomic add — also needs to drive equipment list / toast
  'container/setExpansionSpeakerType',      // Expansion speaker type selection triggers layout recalc
  'container/setExpansionSpeakerModel',     // Speaker model swap triggers recalc — without this the reducer wipes the layout (qty:0, layout:[]) but never repopulates it, so the canvas drops the icons
  'container/setExpansionSpeakers',         // PIG-610: expansion pendant Height field dispatches this — the new height feeds the coverage radius (spec §6 Step 2), so placement/qty must re-run
  'container/setExpansionSpeakerCoverage',  // Expansion speaker coverage change triggers layout recalc
  'container/addExpansionSpeaker',          // Manual + click adds an expansion speaker — equipment list + toast need to see the new speaker count
  'container/removeExpansionSpeaker',       // Manual − click removes one — same reason
  'ui/setCurrentView',
];

/**
 * PIG-513: actions after which the table-expansion mic layout may legitimately be re-placed.
 * For any OTHER recalc, already-placed table expansion mics are preserved (see the
 * `isExpansionPreserved` guard in performDeviceCalculation). These are the only triggers that
 * change either the expansion configuration or the geometry the placement is derived from:
 *   - expansion config edits (type / coverage / density fader / manual add-remove-place)
 *   - table & room geometry (setTable, setDimensions)
 *   - acoustics (changes the TTM-X coverage radius → mic count)
 *   - primary bar model (setMicrophones re-evaluates the bar gap / expansion recommendation)
 *   - area reset / undo (resetArea)
 */
const EXPANSION_REPLACE_TRIGGERS = new Set<string>([
  'container/setExpansionMicType',
  'container/setExpansionMicCoverage',
  'container/setExpansionMicrophones',
  'container/addExpansionMic',
  'container/removeExpansionMic',
  'container/placeExpansionMicAtLocation',
  'container/setTable',
  'container/setDimensions',
  'container/setAcoustics',
  'container/setMicrophones',
  'container/resetArea',
]);

/**
 * PIG-513: decide whether the already-placed table-expansion mics must be preserved for this
 * recalc. Returns true (preserve, i.e. skip the path-based re-placement) when mics are already
 * placed AND the triggering action is not one of the EXPANSION_REPLACE_TRIGGERS. With no mics
 * placed yet there is nothing to protect, so first-time placement always runs. Exported so the
 * decision can be unit-tested without the DOM / SVG-path machinery the placement itself needs.
 */
export const shouldPreserveExpansionMics = (actionType: string, areaState: any): boolean =>
  (areaState?.expansionMicrophones?.layout?.length ?? 0) > 0 &&
  !EXPANSION_REPLACE_TRIGGERS.has(actionType);

/**
 * Table-expansion scenario (spec §7 Step 2) as it stood BEFORE the in-flight acoustics change,
 * sampled pre-dispatch against the pre-repaint `#micPath`.
 *
 * Why a module-level stash rather than a state field: the scenario is not persisted anywhere
 * (`calcExpansionTableMicPlacement` returns it, but `TableExpansionResult` discards it and
 * `setExpansionMicLayout` stores only layout/qty), and the acoustics tier drives BOTH the bar
 * reach and the path's device-to-edge inset — so once `setAcoustics` has been reduced and React
 * has repainted, the previous scenario is no longer recoverable from anything observable.
 * `container/setAcoustics` is in `needsDOMUpdate`, so the placement pass that consumes this runs
 * a couple of animation frames later; the value has to survive that gap.
 *
 * Carries the area it was sampled in (the stash is module-global, the scenario is per-area) and
 * the path length it saw, so the consuming pass can reject a stash that doesn't describe the
 * geometry it is now looking at.
 *
 * Lifecycle: written on EVERY `container/setAcoustics` that reaches the middleware body — with
 * `scenario: null` when the area isn't a table-expansion design — so it can never carry a value
 * forward from an older change. Consumed only under `isAcousticsChange`. Several paths set it
 * without ever consuming it (a blocking error at `ui.error['130']`, an open
 * `expansionMicRecommendation` modal, or `performDeviceCalculation` bailing early), and the next
 * acoustics change simply overwrites it. The consequence of every drop is the same: the
 * transition is MISSED for that one change, never applied spuriously. That is the safe direction
 * — a missed removal leaves mics the user can still remove by hand, whereas a spurious one
 * deletes mics silently.
 */
type AcousticsScenarioStash = {
  areaId: string | undefined;
  /** Table expansion: spec §7 Step 2 scenario number. */
  scenario: number | null;
  /** Ceiling expansion: `computeCeilingExpansionOutcome` result. */
  ceilingOutcome: CeilingExpansionOutcome | null;
  /**
   * `#micPath` length the TABLE probe sampled. Ceiling doesn't need it — its probe is pure
   * geometry off `area` + the coverage boxes, with no DOM to be stale.
   */
  pathLength: number | null;
};

let pendingAcousticsPriorScenario: AcousticsScenarioStash | null = null;

export const setAcousticsPriorScenario = (stash: AcousticsScenarioStash | null): void => {
  pendingAcousticsPriorScenario = stash;
};

/**
 * Take the stashed prior scenario, but only if it describes the area and the path geometry this
 * pass is looking at.
 *
 * The path-length check is the guard against the one destructive failure mode: the pre-dispatch
 * probe has no "path is ready" wait (unlike the consuming pass, which goes through the rAF retry
 * loop), so right after a remount it can sample a stale `#micPath` and read Scenario 3 where the
 * truth is 1 — producing a silent removal the user never asked for. An acoustics change reshapes
 * the path via `deviceToEdge`, so the two lengths legitimately differ; what we require is only
 * that the probe saw SOMETHING, and we drop the stash when it saw nothing.
 */
export const consumeAcousticsPriorScenario = (areaId: string | undefined): number | null => {
  const stash = pendingAcousticsPriorScenario;
  pendingAcousticsPriorScenario = null;
  if (!stash || stash.areaId !== areaId) return null;
  if (stash.pathLength == null || !(stash.pathLength > 0)) return null;
  return stash.scenario;
};

/**
 * Ceiling counterpart of `consumeAcousticsPriorScenario`. Same area check, but no path-length
 * guard — the ceiling probe never touches the DOM, so there is no stale-geometry failure mode to
 * defend against.
 *
 * Note both consumers clear the stash, so whichever expansion type is active takes it and the
 * other gets null on a second call. That is correct: an area has one `expansionMicType`, so only
 * one of the two branches can be live.
 */
export const consumeAcousticsPriorCeilingOutcome = (areaId: string | undefined): CeilingExpansionOutcome | null => {
  const stash = pendingAcousticsPriorScenario;
  pendingAcousticsPriorScenario = null;
  if (!stash || stash.areaId !== areaId) return null;
  return stash.ceilingOutcome;
};

/**
 * Does this acoustics change move the table expansion from "coverage required" to "coverage not
 * required" (spec §7 Step 2: Scenario 3 → Scenario 1)?
 *
 * That specific transition is the one where the placed mics stop being justified at all: the bar
 * now covers the whole table path on its own, so the design should return to the state it would
 * have been in had the user never been prompted — no mics, and the mic-selector control back to
 * "Add". Every other transition still wants mics, just re-placed at the new TTM-X radius, and is
 * handled by the ordinary placement pass.
 *
 * Scenario 2 is deliberately NOT treated as "coverage required" here. The bar not reaching the
 * table at all is a standard standalone TTM placement (spec §7 Step 8), not an expansion arc,
 * and both the spec and the existing code route Scenario 1 and 2 to the same full-path
 * placement — so a 2 → 1 move is not a change in whether mics are wanted.
 */
export const isCoverageNoLongerRequired = (
  priorScenario: number | null,
  currentScenario: number | null,
): boolean => priorScenario === 3 && currentScenario === 1;

/**
 * The three outcomes ceiling expansion placement can produce. A union rather than a bare string so
 * the compiler catches a typo in the comparisons below — same reasoning as the middleware flag
 * types in `types/middleware/actionMeta.ts`.
 */
export type CeilingExpansionOutcome = 'placed' | 'full-coverage' | 'below-min-residual';

/**
 * Ceiling expansion's answer to "what did the placement decide", collapsed to the three outcomes
 * a caller can act on:
 *
 *   - `'placed'`              — a real residual exists and mics were placed for it.
 *   - `'full-coverage'`       — edge case A: the bar covers the entire base box. The ceiling
 *                               analogue of table Scenario 1.
 *   - `'below-min-residual'`  — edge case B: a gap exists but is shallower than radius × √2.
 *
 * `calcBarExpansionPlacement` returns `{ coords: [], qty: 0 }` for both edge cases, so without
 * the `reason` discriminator the two are indistinguishable here — and they mean opposite things:
 * A says the expansion config is no longer wanted, B says only this particular gap isn't worth a
 * mic. Returns null when the outcome can't be determined (no geometry, unknown bar model).
 *
 * Unlike the table probe this is pure geometry — it reads `area` and the coverage boxes, never
 * the DOM — so there is no repaint race and no need for a path-readiness guard.
 */
export const computeCeilingExpansionOutcome = (state: any, areaState: any): CeilingExpansionOutcome | null => {
  const constants = toolIndex.conferenceRoomDesigner?.constants;
  // `dimensions`/`table` are dereferenced unguarded inside `getCoverageBoxes`, so an area missing
  // them must bail here rather than throw out of the middleware. Mirrors the table probe's guard.
  if (areaState?.location == null || areaState?.dimensions == null) return null;

  const isFocused = getExpansionCeilingIsFocused(areaState);
  const baseBox = getCoverageBoxes(state)?.[isFocused ? 'focused' : 'full_room'];
  if (!baseBox) return null;

  const acousticIndex = Math.max(0, Math.min(4, constants?.acoustics?.indexOf(areaState.acoustics ?? 'good') ?? 2));
  // Same height resolution `computeExpansionCeilingMicCoords` uses — the expansion group's own
  // configured mount height, NOT `microphones.height` (the primary is a wall/table-mounted bar).
  // It feeds the DCM-X coverage radius, which feeds the minimum-residual test, so a different
  // height here could disagree with the placement this outcome describes.
  const expansionMicHeight = areaState.expansionMicrophones?.height
    ?? areaState.ceilingHeight?.min
    ?? areaState.ceilingHeight
    ?? 2.8956;

  const result: any = calcExpansionCeilingMicPlacement({
    room: { left: areaState.location.left },
    baseBox,
    acousticIndex,
    isFocused,
    barModel: areaState.microphones?.model,
    ceilingHeight: expansionMicHeight,
  });

  if (!result) return null;
  if (result.qty > 0) return 'placed';
  // A future empty return that forgets `reason` degrades to null → transition missed. That is the
  // safe direction (mics kept), but silent — hence the union type above.
  return (result.reason as CeilingExpansionOutcome) ?? null;
};

/**
 * Does this acoustics change move the CEILING expansion into "coverage not required"?
 *
 * `'below-min-residual'` is excluded as the CURRENT outcome: it means the gap merely got too small
 * to be worth a mic, not that it closed — the expansion config is still meaningful, and removing
 * it would delete the user's configuration because their gap shrank.
 *
 * As the PRIOR outcome it is deliberately INCLUDED, which is the one place this does not mirror
 * `isCoverageNoLongerRequired`. Stepping the tiers one at a time can pass through a below-minimum
 * intermediate: on the default room, poor places mics, good goes below-minimum (the layout is
 * emptied by `computeExpansionCeilingMicCoords`), great reaches full coverage. Requiring
 * `'placed'` on the prior side would let that middle step permanently destroy the transition and
 * strand the design at `expansionMicType: 'ceiling'` with an empty layout, a "Remove" button with
 * nothing to remove, and the UCX lock still pinned — exactly the state this rule exists to
 * prevent. The table path has no such hole: its scenario number stays 3 through a
 * below-minimum-viable arc, so the intermediate tier doesn't erase the prior side.
 *
 * The TOAST is gated separately, on mics having actually been on screen — see the dispatch site.
 * Resetting the type after a silent intermediate emptying is bookkeeping, not a disappearance,
 * and announcing "expansion microphones removed" for it would describe something the user never
 * saw happen.
 */
export const isCeilingCoverageNoLongerRequired = (
  priorOutcome: CeilingExpansionOutcome | null,
  currentOutcome: CeilingExpansionOutcome | null,
): boolean => priorOutcome != null
  && priorOutcome !== 'full-coverage'
  && currentOutcome === 'full-coverage';

/**
 * Monotonically increasing token identifying the MOST RECENTLY SCHEDULED deferred
 * (`needsDOMUpdate`) recalculation pass — see its use in the `needsDOMUpdate` branch below.
 *
 * Bug this fixes: `attemptCalculation`'s rAF/#micPath-poll loop can still be waiting (up to
 * 2 rAFs + 10×20ms retries, ~200ms) when the user triggers ANOTHER recalculation-worthy
 * action (e.g. switching acoustics again, or — confirmed via a real-timer regression test —
 * an EARLIER action's own still-pending deferred pass, such as `setExpansionMicType`,
 * resolving only after a LATER `setAcoustics` has already been reduced). Each pass reads
 * fresh state via `getState()` right before it finally runs, but keeps the ORIGINAL
 * `action` object from whichever dispatch scheduled it — so an overtaken pass can run
 * `performDeviceCalculation` with a stale `action.type` paired against state that has
 * since moved past it (e.g. a stray `setExpansionMicType` context after the current
 * acoustics change already applied the Devio UCX capacity fallback), producing a second,
 * redundant — and in the general case DIFFERENT — `getDeviceLayouts` pass and dispatch
 * sequence for what the user experienced as a single change.
 *
 * Every entry into the `needsDOMUpdate` branch bumps this and captures the new value; each
 * scheduled `attemptCalculation` bails out as soon as a NEWER pass has been scheduled,
 * leaving exactly one (the latest) pass to actually run `performDeviceCalculation`.
 */
let latestDeferredCalculationToken = 0;

/**
 * Actions that should NOT trigger recalculation
 */
const SKIP_RECALCULATION = [
  'ui/setError',
  'ui/setShowHeatmap',
  'ui/setShowDevices',
  'ui/setNeedsResave',
  'container/setCollisions',
  'container/setEquipmentList',
  'container/setExpansionMicRecommended', // Set by gap check — must not re-trigger recalc
  'container/setExpansionMicSuppressed',  // Set by the placement pass itself — must not loop
  'container/setExpansionMicLayout',      // Set after expansion mic calculation — must not loop
  'container/setExpansionSpeakerLayout',  // Set after expansion speaker calculation — must not loop
];

/**
 * Check if an action should trigger device recalculation
 */
const shouldRecalculate = (action: AnyAction): boolean => {
  // Check if action is in skip list first (for performance)
  if (SKIP_RECALCULATION.some(skip => action.type.includes(skip))) {
    return false;
  }

  // Skip recalc for actions that were dispatched by this middleware itself
  if (action.payload?._fromSyncMiddleware &&
      (action.type.includes('updateDevice')
        || action.type.includes('setExpansionSpeakerType')
        || action.type.includes('setExpansionMicrophones'))) {
    return false;
  }

  // Check if action should trigger recalculation
  const shouldRecalc = RECALCULATION_TRIGGERS.some(trigger => action.type.includes(trigger));
  return shouldRecalc;
};

/**
 * The single action type that triggers the warn-and-confirm intercept when the area
 * has manual edits. Earlier iterations broadened this to ceiling height, acoustics,
 * table, and model swaps, but the dialog fired too often (table moves, ghost-chair
 * toggles, sub-model swaps) — narrowed back to dimensions, the highest-impact reset.
 * Other resets are visible and easy to undo via the Undo button.
 */
const CONFIRM_INTERCEPT_ACTION = 'container/setDimensions';

/**
 * Check if a removeDevice action will be blocked by errorMiddleware
 */
const willRemoveDeviceBeBlocked = (state: any, action: AnyAction): boolean => {
  if (!action.type.includes('removeDevice')) return false;
  
  const deviceType = action.payload?.device || state.ui?.currentDevice || state.container?.currentDevice;
  const activeTab = state.container?.activeTab;
  if (!deviceType || !activeTab) return false;
  
  const deviceQty = state.container?.areas?.[activeTab]?.[deviceType]?.qty || 0;
  return deviceQty <= 1; // Will be blocked if removing would leave 0 devices
};

/**
 * PIG-295 AC2/AC3: When a BMA360D would compute an invalid (NaN / ≤ 0) or sub-1 m
 * coverage radius at its current height, lower it to the maximum allowable height
 * for the current acoustic level and notify the user.
 *
 * Mirrors the radius formula in roomCalculations.ts (line ~919-920):
 *   verticalDrop = micHeight − talkerHeight
 *   radius = √(maxDist² − verticalDrop²)   (returns 0 on negative difference)
 *
 * Returns true if a height adjustment was dispatched (caller should let the resulting
 * recalc-trigger action drive the next layout pass). Returns false otherwise.
 */
const MIN_RADIUS_M = 1.0;
export const maybeAutoLowerBma360d = (dispatch: any, state: any): boolean => {
  const constants = toolIndex[state.ui?.tool]?.constants;
  if (!constants) return false;
  const currentArea = state.container?.activeTab;
  const area = state.container?.areas?.[currentArea];
  if (!area?.microphones?.model || !area.ceilingHeight || !area.acoustics) return false;

  const micModel = area.microphones.model;
  const CEILING_TILE = constants.equipment?.micCategories?.CEILING_TILE;
  if (!CEILING_TILE?.has(micModel)) return false;

  const micEquipment = constants.equipment.microphones[micModel];
  if (typeof micEquipment?.maxAllowableHeight !== 'function') return false;

  const talkerHeight = constants.area?.talkerHeight ?? 1.2009;
  const acousticsIndex = constants.acoustics?.indexOf(area.acoustics);
  if (acousticsIndex == null || acousticsIndex < 0) return false;
  const maxDist = micEquipment.micData?.[acousticsIndex];
  if (typeof maxDist !== 'number') return false;

  const currentHeight = area.microphones.height;
  if (typeof currentHeight !== 'number') return false;
  const verticalDrop = currentHeight - talkerHeight;
  // `side` returns 0 on negative difference (vertical drop ≥ maxDist) — same as roomCalculations.
  const radius = round(side(maxDist, verticalDrop), 4);
  if (radius >= MIN_RADIUS_M && Number.isFinite(radius)) return false;

  const newHeight = micEquipment.maxAllowableHeight(area, MIN_RADIUS_M);
  if (typeof newHeight !== 'number') return false;
  // Use a small tolerance so floating-point round-trip from the reducer doesn't re-fire
  // the auto-lower on the next pass when the result is essentially equal to current.
  if (newHeight >= currentHeight - 0.001) return false;

  const isMeter = state.ui?.units === 'meters';
  showWarning(
    `BMA360D has been lowered to ${distanceToString(newHeight, isMeter)} to maintain coverage at the selected acoustic level.`,
  );
  const syncMeta: MiddlewareActionMeta = { _fromSyncMiddleware: true };
  dispatch(ReducersIndex.container.setMicrophones({ height: newHeight, ...syncMeta } as any));
  return true;
};

// -----------------------------------------------------------------------------
// Table-expansion mic placement strategies
//
// Two interchangeable algorithms produce `{ coords }` for bar + table-expansion
// configurations. The active strategy is selected via `tableExpansionStrategy` below
// — swap to flip behaviour without touching the middleware body.
//
// Each strategy is a self-contained `(state, areaState) => { coords }` so the
// middleware doesn't need to know which one is running. Both must return coords
// in the table-mic shape (model: 'ttm_x', modelName: DTM-X placeholder, z=0.91,
// horAngle: 0) so downstream rendering and equipment summary work identically.
// -----------------------------------------------------------------------------

type ExpansionMicDensity = { value: number; jumpPoints: number[]; overlaps: number[]; context?: string };

/**
 * Identifies the conditions a `coverageDensity.value` default was derived from, so a later recalc
 * can tell "the user deliberately picked this" from "this is just the last default we computed".
 * Mirrors the `context` stamp `roomCalculations.ts` puts on the PRIMARY mic's density (PIG-290),
 * but keyed on acoustics as well as model: the expansion count is
 * `effectiveLen / (defTableOverlap × ttmRadius)`, and ttmRadius comes straight from
 * `micData[acousticIndex]` — so a change of acoustics invalidates the previous default outright.
 */
export const expansionDensityContext = (model: string | undefined, acousticIndex: number): string =>
  `${model ?? 'ttm_x'}|${acousticIndex}`;

/**
 * Should a stored `coverageDensity.value` be reused for this calculation?
 *
 * Mirrors `resolveCeilingMicDensity` (roomCalculations.ts), including the reason it does NOT
 * settle for a membership test: "a stale higher count is almost always still a member of the
 * smaller room's jumpPoints, so a membership-only check is not enough". The expansion path used
 * exactly that insufficient check, which is how a 2 computed at Good survived into Great and
 * Perfect (both offer jumpPoints [1, 2], so 2 stayed 'valid' while the real default fell to 1).
 *
 * A missing stored context is treated as a match so designs saved before this stamp existed keep
 * honouring their slider position on first recalc.
 */
export const shouldReuseStoredDensity = (
  stored: Partial<ExpansionMicDensity> | undefined,
  fresh: ExpansionMicDensity,
): boolean => {
  if (typeof stored?.value !== 'number' || !fresh.jumpPoints.includes(stored.value)) return false;
  const contextMatches = stored.context == null || stored.context === fresh.context;
  const prev = stored.jumpPoints ?? [];
  const geometryUnchanged = prev.length === fresh.jumpPoints.length
    && prev.every((v, i) => v === fresh.jumpPoints[i]);
  return contextMatches && geometryUnchanged;
};
type TableExpansionResult = {
  coords: any[];
  density?: ExpansionMicDensity;
  requestedQty?: number;
  /**
   * Spec §7 Step 2 scenario the placement actually ran, or null when none could be determined
   * (no `#micPath`, no geometry, unknown bar model). `coords: []` alone cannot distinguish
   * "Scenario 3 whose residual is below minimum viable" from "the calculation never ran", and
   * those want opposite treatment — the first is a real result worth explaining to the user, the
   * second is a DOM-timing failure that must not be explained as one.
   */
  scenario?: number | null;
};
type TableExpansionStrategy = (state: any, areaState: any) => TableExpansionResult;

/**
 * Table-mic spacing factor (`defTableOverlap`, spec §5 = 1.34): mic count = length /
 * (defTableOverlap × radius). Read from constants so the value isn't duplicated as a literal
 * (spec open question T.6). Falls back to 1.34 if constants are unavailable (test envs).
 */
const tableMicSpacingFactor = (): number =>
  toolIndex.conferenceRoomDesigner?.constants?.defTableOverlap ?? 1.34;

/**
 * Standalone TTM-X placement along the table's micPath — mirrors the WDT-86 spec's
 * Scenario 2 directive ("standard standalone TTM placement applies unchanged"). Evenly
 * distributes mics along the path at `defTableOverlap × ttmRadius` density, the same factor
 * `calculateMicPlacement` uses for standalone tabletop mics in `roomCalculations.ts`.
 * Flat (collapsed) paths sample only the outbound half to avoid duplicating positions
 * across the doubled-back leg.
 */
const placeStandaloneTableMics = (
  path: SVGPathElement,
  bbox: { x0: number; y0: number },
  scale: number,
  isFlat: boolean,
  ttmRadiusM: number,
  maxExpansionMics: number,
  ttmModelName: string | undefined,
  forcedCount?: number,
): any[] => {
  const totalLengthPx = path.getTotalLength();
  const totalLengthM = totalLengthPx / scale;
  const halving = isFlat ? 0.5 : 1;
  const effectiveLenM = totalLengthM * halving;
  // Spec §7 Step 8: `idealQty = effectiveLen / (defTableOverlap × micCoverageRadius)` — no +1.
  // The spec's `(isFlat || isEndTable ? 1 : 0)` is a fencepost term on the SPACING divisor (N mics
  // along an open line span N−1 gaps), not on the count. It used to be added here, where it
  // exactly cancelled the × 0.5 halving above — which is why fixing `isFlat` alone changed
  // nothing. Legacy `roomCalculations.ts` still carries the old form for ordinary standalone
  // tabletop mics; that path is deliberately untouched (see the note in the flat-sampling comment
  // below — it has the same latent defect and needs the same treatment).
  const rawCount = effectiveLenM / (tableMicSpacingFactor() * ttmRadiusM);
  const defaultQty = Math.max(1, Math.min(maxExpansionMics, Math.round(rawCount)));
  const micQty = forcedCount != null
    ? Math.max(1, Math.min(maxExpansionMics, Math.round(forcedCount)))
    : defaultQty;

  // Apply CTM so sampled path points land in viewport coords matching bbox/scale.
  const ctm = (path.getCTM && typeof path.getCTM === 'function') ? path.getCTM() : null;
  const toViewport = ctm
    ? (p: any) => ({ x: ctm.a * p.x + ctm.c * p.y + ctm.e, y: ctm.b * p.x + ctm.d * p.y + ctm.f })
    : (p: any) => p;

  // Sample the path at fraction `t`, resolving the flat case onto the table's CENTRELINE.
  //
  // Spec §4.1's `isFlat` says "the path doubles back on itself" — the two long edges are within a
  // mic diameter of each other, so one row of mics covers both. Two consequences follow, and they
  // are a package: the parametric length counts the table twice (the × 0.5 halving above), AND the
  // out and return legs describe the SAME row. Taking only t ∈ [0, 0.5] gets the halving right but
  // puts every mic on ONE edge — for the default keystone, segment P0→P1 alone is 48.4% of the
  // perimeter, so all of them land along the top edge. That is the bug this comment exists to
  // prevent recurring; it is what shipped when the halving was enabled on its own.
  //
  // Instead, pair each sample with its mirror at `1 − t`: on a doubled-back path those are the two
  // long edges at the same station along the table, so their midpoint is the exact centreline (x
  // agrees by symmetry, y averages to the table's centre). Shape-agnostic — no table-specific
  // geometry needed — and it leaves the shared #micPath element untouched, which matters because
  // the primary tabletop mic and the ceiling expansion mic sample the same element with different
  // coverage radii (see roomCalculations.ts:2934 and :3139).
  const sampleAt = (t: number) => {
    const a = toViewport(path.getPointAtLength(t * totalLengthPx));
    if (!isFlat) return a;
    const b = toViewport(path.getPointAtLength((1 - t) * totalLengthPx));
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  };

  return Array.from({ length: micQty }, (_, i) => {
    const t = ((i + 0.5) / micQty) * halving;
    const pt = sampleAt(t);
    return {
      model: 'ttm_x',
      modelName: ttmModelName,
      location: {
        x: (pt.x - bbox.x0) / scale,
        y: (pt.y - bbox.y0) / scale,
        z: TABLE_MIC_Z_HEIGHT,
      },
      horAngle: 0,
    };
  });
};

/**
 * Compute the Coverage Density jumpPoints + default value for manual-add table
 * expansion. Mirrors the path-length / TTM-X radius math `placeStandaloneTableMics`
 * uses for the default count, then expands it into a discrete selectable range
 * [1 .. maxViable] (capped at maxExpansionMics) so the fader has marks to snap to.
 *
 * `overlaps` is a per-jumpPoint sidecar that the Fader UI doesn't actually render
 * here — included for shape compatibility with `microphones.coverageDensity`,
 * which the Fader's selector path expects.
 */
const computeTableExpansionDensity = (
  totalLengthM: number,
  isFlat: boolean,
  ttmRadiusM: number,
  maxExpansionMics: number,
  context?: string,
): ExpansionMicDensity => {
  const halving = isFlat ? 0.5 : 1;
  const effectiveLenM = totalLengthM * halving;
  // No +1 — see the spec §7 Step 8 note in `placeStandaloneTableMics`. These two formulas must
  // stay in lockstep or the fader's default value won't match the count actually placed.
  const rawCount = effectiveLenM / (tableMicSpacingFactor() * ttmRadiusM);
  const defaultQty = Math.max(1, Math.min(maxExpansionMics, Math.round(rawCount)));
  // Upper bound uses ceil (not round) so the slider can offer one "denser than default"
  // step when rawCount falls between integers — e.g. rawCount = 2.3 → defaultQty = 2,
  // maxQty = 3. Without this the user could only ever tune down from the default.
  // `Math.max(defaultQty, ...)` guards the rare case where round > ceil isn't possible
  // (it isn't), but also keeps maxQty ≥ defaultQty when rawCount is already an integer.
  const maxQty = Math.max(defaultQty, Math.min(maxExpansionMics, Math.ceil(rawCount)));
  const jumpPoints = Array.from({ length: maxQty }, (_, i) => i + 1);
  return { value: defaultQty, jumpPoints, overlaps: jumpPoints.map(() => 1), context };
};

/**
 * Derive every input the table-expansion scenario check and the placement itself share, read
 * from the current state plus `#micPath` as the DOM holds it RIGHT NOW. Returns null when the
 * path is unavailable or collapsed, or the area has no geometry yet — all of which both callers
 * treat as "no scenario / place nothing".
 *
 * Factored out so the acoustics-transition probe (`computeTableExpansionScenario`) samples the
 * path with exactly the same geometry, bar position and shape flags the real placement uses. A
 * probe that derived these independently could disagree with the placement it gates, which is
 * the one failure mode that would make the transition rule below misfire.
 */
const deriveTableExpansionInputs = (state: any, areaState: any) => {
  const constants = toolIndex.conferenceRoomDesigner?.constants;
  const micPath = document.getElementById('micPath') as unknown as SVGPathElement | null;
  // `!(len > 0)` rather than `len <= 0` — the exact inversion of the original guard. A NaN length
  // fails both comparisons, and `<=` would let it through into sampling.
  if (!micPath || !(micPath.getTotalLength() > 0)) return null;
  // Geometry the bar position is derived from. Previously this threw a TypeError when absent;
  // returning null instead makes both callers treat it as "no scenario / place nothing".
  if (areaState?.location == null || areaState?.dimensions == null) return null;

  const bbox = getBoundingBox(state);
  const scale = getScale(state);
  const micModel = areaState.microphones?.model;
  const acousticIndex = Math.max(0, Math.min(4, constants?.acoustics?.indexOf(areaState.acoustics ?? 'good') ?? 2));
  const shape = areaState.table?.shape;
  const isEndTable = constants?.tables?.[shape]?.endTable ?? false;
  // Only the bullet has a genuinely redundant return leg (parallel edges → flat centreline).
  // keystone DIVERGES like vshape/ushape, so both arms need expansion coverage — don't clamp.
  // (logic.js re-resolves this from `shape` too; kept here for the non-shape callers.)
  const returnLegRedundant = shape === 'bullet';

  const ttmRadiusM = constants?.equipment?.microphones?.ttm_x?.micData?.[acousticIndex] ?? 1.7;
  const maxExpansionMics = constants?.maxExpansionMics ?? 4;

  // isFlat per spec §4.1: `(tableHeight − 2 × INSET) ≤ micCoverageRadius × 2` — the two long
  // edges of the mic path are within a mic diameter, so the path effectively doubles back on
  // itself and one row of mics covers both. `placeStandaloneTableMics` consumes this for BOTH
  // its consequences: the × 0.5 length correction and the mirrored centreline sampling.
  //
  // Evaluated from the table's own dimensions rather than inferred from a zero-area bbox. The
  // bbox only flattens once `devicePathProps` has zeroed devicePathWidth (tableConstants.ts),
  // and that branch is gated on `!isTableMic` while #micPath is always built with
  // isTableMic=true — so it is unreachable and the bbox test was permanently false. Fixing it
  // there instead is NOT an option: #micPath is shared by the primary tabletop mic and the
  // ceiling expansion mic (roomCalculations.ts:2934 and :3139), each with a different coverage
  // radius, so collapsing the element itself would change placement for all three consumers.
  //
  // The bbox test is retained as a fallback: a path that HAS genuinely collapsed is flat
  // regardless of what the dimensions say.
  //
  // NOTE: `roomCalculations.ts` still derives its own isFlat from the same unreachable bbox
  // collapse, so its centreline-forcing branch (:3195-3196) remains dead for ordinary
  // standalone tabletop mics. Same defect, same fix shape — not addressed here.
  const tableWidthM = areaState.table?.size?.width ?? 0;
  const insetM = MuiTableConstants.deviceToEdge(true, acousticIndex);
  let isFlat = tableWidthM > 0 && (tableWidthM - 2 * insetM) <= ttmRadiusM * 2;
  if (!isFlat) {
    try { const pb = micPath.getBBox(); isFlat = pb.width * pb.height === 0; } catch (_) { /* not supported in all envs */ }
  }

  // Bar position in canvas pixels: fixed wall offset, vertical room centre
  const wallOffset = constants?.equipment?.wallMountedBarOffset ?? 0.134;
  const barRoomX = areaState.location.left + wallOffset;
  const barRoomY = areaState.location.top + areaState.dimensions.width / 2;
  const barPosition = { x: bbox.x0 + barRoomX * scale, y: bbox.y0 + barRoomY * scale };

  return {
    constants, micPath, bbox, scale, micModel, acousticIndex, shape,
    isEndTable, returnLegRedundant, ttmRadiusM, maxExpansionMics, isFlat, barPosition,
  };
};

/**
 * Scenario number (spec §7 Step 2) for the table expansion mics, sampled against the path as
 * the DOM currently holds it. Returns null when it can't be determined (no path, no geometry,
 * unknown bar model).
 *
 * Deliberately cheap: `scenarioOnly` stops `calcExpansionTableMicPlacement` after Step 2, so a
 * Scenario 3 probe doesn't pay for tiling output that gets thrown away.
 */
export const computeTableExpansionScenario = (state: any, areaState: any): number | null => {
  const inputs = deriveTableExpansionInputs(state, areaState);
  if (!inputs) return null;
  const probe: any = calcExpansionTableMicPlacement({
    acousticIndex: inputs.acousticIndex,
    barModel: inputs.micModel,
    path: inputs.micPath,
    barPosition: inputs.barPosition,
    scale: inputs.scale,
    bbox: inputs.bbox,
    isFlat: inputs.isFlat,
    isEndTable: inputs.isEndTable,
    returnLegRedundant: inputs.returnLegRedundant,
    shape: inputs.shape,
    scenarioOnly: true,
  });
  return typeof probe?.scenario === 'number' ? probe.scenario : null;
};

/**
 * Path-based table expansion (WDT-86 spec implementation).
 * Samples the SVG micPath rendered by MuiTable against the bar's coverage circle and
 * places ttm_x mics in the uncovered arc with `defBarTableOverlap` pull-back. DOM-coupled
 * (reads `#micPath` from the document), which is why this lives in the middleware rather
 * than in `roomCalculations.ts`.
 *
 * This runs only when the user has added table expansion (`expansionMicType === 'table'`,
 * i.e. they clicked "Add"). Placement is then SCENARIO-driven, per spec §2.1/§2.2 — the
 * scenario alone decides; there is no separate manual-opt-in flag for table expansion:
 *
 *   - **Scenario 1** — Add + no coverage required (the bar covers the whole table path):
 *     standard standalone placement on the full path (spec §2.1).
 *   - **Scenario 2** — bar doesn't reach the table at all: same standard full-path placement
 *     (spec §7 Step 8).
 *   - **Scenario 3** — Add + coverage required (partial overlap): reduced-arc placement
 *     (`calcExpansionTableMicPlacement`, spec §7 Steps 3–7), for every table shape.
 *
 * One transition is handled by the caller rather than here: an acoustics change that moves the
 * design from Scenario 3 to Scenario 1 removes the mics entirely instead of re-placing them on
 * the full path, because the bar now covers the table unaided. See the transition block at the
 * dispatch site in `performDeviceCalculation`.
 *
 * Two known defects live in here and are documented at their sites below: the unreachable
 * path-collapse that pins `isFlat` to false, and the scenario-number branch that strands a
 * below-minimum-viable Scenario 3 at zero mics. The first is a prerequisite for fixing the
 * second — see the comments on each.
 *
 * Full-path placements also seed the Coverage Density fader (jumpPoints + default count).
 * NOTE: the reduced-arc path (Scenario 3) does not yet emit jumpPoints (spec §8
 * `updatedJumpPoints`) — wiring the fader to the arc in Scenario 3 is a follow-up.
 */
const computeTableExpansionViaPath: TableExpansionStrategy = (state, areaState) => {
  const ttmModelName = toolIndex.conferenceRoomDesigner?.constants?.expansionMicPlaceholders?.table

  let coords: any[] = [];
  let density: ExpansionMicDensity | undefined;
  let requestedQty: number | undefined;
  let scenario: number | null = null;
  // Derivation (path, bbox/scale, acoustics-indexed TTM radius, isFlat, bar position, shape
  // flags) is shared with `computeTableExpansionScenario` — see `deriveTableExpansionInputs`.
  const inputs = deriveTableExpansionInputs(state, areaState);
  if (inputs) {
    const {
      micPath, bbox, scale, micModel, acousticIndex, shape,
      isEndTable, returnLegRedundant, ttmRadiusM, maxExpansionMics, isFlat, barPosition,
    } = inputs;

    const result: any = calcExpansionTableMicPlacement({
      acousticIndex,
      barModel: micModel,
      path: micPath,
      barPosition,
      scale,
      bbox,
      isFlat,
      isEndTable,
      returnLegRedundant,
      shape, // authoritative: logic.js re-resolves isEndTable/returnLegRedundant from this
    });

    // Standard standalone TTM placement on the full path (spec §7 Step 8). Also seeds the
    // Coverage Density fader (jumpPoints + default count) so it has data where it applies.
    const placeFullPath = () => {
      const totalLengthM = micPath.getTotalLength() / scale;
      const densityContext = expansionDensityContext('ttm_x', acousticIndex);
      density = computeTableExpansionDensity(totalLengthM, isFlat, ttmRadiusM, maxExpansionMics, densityContext);
      const stored = areaState.expansionMicrophones?.coverageDensity;
      const selectedCount = shouldReuseStoredDensity(stored, density) ? stored.value : density.value;
      return placeStandaloneTableMics(micPath, bbox, scale, isFlat, ttmRadiusM, maxExpansionMics, ttmModelName, selectedCount);
    };

    // Placement is driven by SCENARIO, per spec §2.1/§2.2. Reaching this strategy already
    // means the user added table expansion (expansionMicType === 'table' — they clicked
    // "Add"), so there is NO separate manual-opt-in gate: the scenario alone decides.
    //
    // KNOWN SPEC DIVERGENCE (unfixed — the fix has a prerequisite, see below). Spec §5 and §8 put
    // Scenario 1 and a below-minimum-viable Scenario 3 in the SAME outcome class: both return
    // `{ coords: [], qty: 0, expansionRequired: false }`, and §5 says so in as many words ("As
    // with Scenario 1..."). Per §2.2 row 2 a user who has added expansion mics should then get
    // standard standalone full-path placement in BOTH cases. Branching on the scenario NUMBER
    // instead strands the below-min-viable case at zero mics with no route to that fallback —
    // a table whose residual gap is merely too SMALL to be worth a mic gets nothing and cannot
    // recover (the menu control has flipped to "Remove" by then), while a table the bar covers
    // COMPLETELY gets a full layout. PIG-513 introduced this branch when it removed the
    // `expansionMicManualOptIn` gate; the ceiling sibling still keys on outcome + opt-in
    // (roomCalculations.ts) and does NOT have the defect.
    //
    // Do NOT fix this by routing the below-min-viable case into `placeFullPath` on its own.
    // `placeFullPath` is itself broken while `isFlat` can never be true (see the isFlat comment
    // above): its count is computed over the un-halved path length, so routing Fair into it on
    // the default keystone yields 4 mics (capped) instead of 2, positioned along the table's top
    // edge rather than its centreline. The path-collapse fix is a PREREQUISITE for this one.
    scenario = typeof result?.scenario === 'number' ? result.scenario : null;
    if (result?.scenario === 3) {
      // Scenario 3 — Add + coverage required (partial overlap): reduced-arc placement
      // (spec §7 Steps 3–7), for all table shapes. Below-minViable → [].
      coords = result?.coords ?? [];
      // requestedQty (pre-maxExpansionMics-cap candidate count) vs coords.length (post-cap) —
      // when they differ, the caller shows a toast explaining the trim (see the dispatch site).
      if (typeof result?.requestedQty === 'number' && result.requestedQty > coords.length) {
        requestedQty = result.requestedQty;
      }
    } else if (result?.scenario === 1 || result?.scenario === 2) {
      // Scenario 1 — Add + no coverage required (bar covers the table): standard standalone
      // placement on the full path (spec §2.1). Scenario 2 — bar doesn't reach the table at
      // all: the same standard full-path placement (spec §7 Step 8).
      coords = placeFullPath();
    } else {
      // No scenario determined (unknown bar model / path unavailable): place nothing.
      coords = [];
    }
  }

  return { coords, density, requestedQty, scenario };
};

/**
 * TEMPORARY ceiling-grid placement for table expansion. Reuses
 * `calcExpansionCeilingMicPlacement`'s even grid sweep over the expansion box, then
 * remaps each coord into the table-mic shape (z=0.91, DTM-X display name). UX preferred
 * this distribution over the path-based bias toward the far table edge in flat-path
 * scenarios. Swap back to `computeTableExpansionViaPath` once the path-based algorithm
 * is revised post-sprint.
 *
 * When the ceiling calc declines (small rooms where the uncovered slice is shorter
 * than tcm_x's `minResidualDepth`), this strategy is the single source of truth — it
 * places one mic anchored to the table's far edge so the user always sees the
 * expansion mic they explicitly added, ON the table.
 */
const computeTableExpansionViaCeilingGrid: TableExpansionStrategy = (state, areaState) => {
  const constants = toolIndex.conferenceRoomDesigner?.constants;
  const micModel = areaState.microphones?.model;
  const acousticIndex = Math.max(0, Math.min(4, constants?.acoustics?.indexOf(areaState.acoustics ?? 'good') ?? 2));
  //const ttmModelName = constants?.expansionMicPlaceholders?.table;
  const ttmModelName = toolIndex.conferenceRoomDesigner?.constants?.expansionMicPlaceholders?.table;
  const coverageBoxes = getCoverageBoxes(state);
  const isFocused = getExpansionCeilingIsFocused(areaState);
  const baseBox = coverageBoxes?.[isFocused ? 'focused' : 'full_room'];

  let coords: any[] = [];
  if (baseBox) {
    const ceilingResult: any = calcExpansionCeilingMicPlacement({
      room: { left: areaState.location.left },
      baseBox,
      acousticIndex,
      isFocused,
      barModel: micModel,
      ceilingHeight: areaState.ceilingHeight?.min ?? areaState.ceilingHeight,
    });
    coords = (ceilingResult?.coords ?? []).map((coord: any) => ({
      ...coord,
      model: 'ttm_x',
      modelName: ttmModelName,
      location: { ...coord.location, z: TABLE_MIC_Z_HEIGHT },
      horAngle: 0,
    }));
  }

  // Fallback: ceiling calc declined to place any mic (small room — bar coverage spans
  // most of the table). Anchor a single mic to the table's far edge so its TTM-X
  // coverage circle reaches the last chair, clamped to stay on the table.
  if (coords.length === 0) {
    const ttmRadius = constants?.equipment?.microphones?.ttm_x?.micData?.[acousticIndex] ?? 1.7;
    const areaLeft = areaState.location?.left ?? 0;
    const areaTop = areaState.location?.top ?? 0;
    const roomLength = areaState.dimensions?.length ?? 0;
    const roomWidth = areaState.dimensions?.width ?? 0;
    const tableLength = areaState.table?.size?.length ?? 0;
    const tableTransformX = areaState.table?.transform?.x ?? 0;
    const tableTransformY = areaState.table?.transform?.y ?? 0;
    const tableCenterX = areaLeft + roomLength / 2 + tableTransformX;
    const tableCenterY = areaTop + roomWidth / 2 + tableTransformY;
    const tableLeft = tableLength > 0 ? tableCenterX - tableLength / 2 : areaLeft;
    const tableRight = tableLength > 0 ? tableCenterX + tableLength / 2 : areaLeft + roomLength;
    // Anchor at tableRight − r, but never further left than the table itself so very
    // narrow tables (length < 2·r) don't push the mic off the surface.
    const fallbackX = Math.max(tableLeft + Math.min(ttmRadius, tableLength / 2 || ttmRadius), tableRight - ttmRadius);
    coords = [{
      model: 'ttm_x',
      modelName: ttmModelName,
      location: { x: fallbackX, y: tableCenterY, z: TABLE_MIC_Z_HEIGHT },
      horAngle: 0,
    }];
  }

  return { coords };
};

/**
 * Active table-expansion placement strategy. Currently the WDT-86 path-based algorithm
 * (`computeTableExpansionViaPath`). Swap to `computeTableExpansionViaCeilingGrid` for
 * the temporary ceiling-grid borrow. Single source of truth — the middleware body only
 * calls this; both algorithms remain available for A/B comparison and quick revert.
 */
const tableExpansionStrategy: TableExpansionStrategy = computeTableExpansionViaPath;

/**
 * Options for a pass the middleware schedules for itself rather than for a user action.
 */
type DeviceCalculationOptions = {
  /**
   * Suppress the Tree Frog amp-port overflow toast for this pass. Set only by the pass forced
   * after the Devio UCX capacity fallback's Case 3b switch (`attemptTableToCeilingSwitch`, see
   * the flag's pickup site below): the user has just been told, in the same beat, that their
   * tabletop expansion mics were swapped for ceiling ones, and this pass exists purely to place
   * those mics. A port-overflow warning stacked on top of that describes the automatic switch's
   * own transitional state, not a choice the user made, and reads as a contradiction of the
   * capacity check that authorized the switch. Any real, persisting overflow still surfaces on
   * the next pass the user's own next action triggers.
   */
  suppressPortOverflowToast?: boolean;
  /**
   * Live store accessor. The `updateDevice` dispatched in this pass can re-resolve the area's
   * Platform / Product Family (`revalidateUcxEligibility` runs once the re-placed speaker count
   * lands — e.g. Tesira + UCC-UCX1000-T fallback ↔ Devio UCX). The equipment list reads those
   * fields back from here so it isn't built from the pre-dispatch snapshot.
   */
  getState?: () => any;
};

/**
 * Same suppression, for the pass that is ALREADY RUNNING when the Case 3b switch happens — which
 * is the usual case and the one `DeviceCalculationOptions` alone cannot reach.
 *
 * `performDeviceCalculation` commits the re-placed TABLE expansion layout partway through its
 * run; that nested `setExpansionMicLayout` dispatch is what trips the capacity fallback and
 * flips the design to ceiling. Execution then returns INTO the middle of that same pass, which
 * carries on to its equipment-list/port-overflow tail and warns — against the `stateForEquipment`
 * snapshot it built from the now-superseded tabletop configuration. So the toast describes a
 * design the fallback has already thrown away, moments after the user was told it was thrown
 * away.
 *
 * Read-and-cleared at the port-overflow check (see there), so it suppresses exactly one pass and
 * cannot leak into a later, legitimate warning if the follow-up pass is superseded before it runs.
 */
let suppressNextPortOverflowToast = false;

/**
 * Perform device layout calculation and dispatch updates.
 * Trigger detection lives in the middleware function below; this function only orchestrates
 * the calculation (delegated to roomCalculations) and the resulting dispatches.
 */
export const performDeviceCalculation = (dispatch: any, state: any, action: AnyAction, options: DeviceCalculationOptions = {}) => {
  const currentArea = state.container?.activeTab;
  const areaState = state.container?.areas[currentArea];

  if (!areaState || isEmpty(areaState)) {
    return;
  }

  // PIG-295 AC2/AC3: when the BMA360D coverage radius would be invalid or sub-1 m at the
  // current height, auto-lower the mic instead of running the layout with a clamped radius.
  // The dispatched setMicrophones is itself a recalc trigger, so layout runs on the fresh
  // (lower) height in the next middleware tick.
  if (maybeAutoLowerBma360d(dispatch, state)) return;

  // Spec §7 Step 2 transition handling for an acoustics change on bar + expansion TABLETOP mics.
  // `priorScenario` was sampled pre-dispatch against the previous tier's path; the current one is
  // sampled here, after the repaint `needsDOMUpdate` waited for.
  //
  // Only 3 → 1 ("coverage required" → "not required") is special: the bar now covers the whole
  // table path unaided, so the expansion mics are removed outright and `expansionMicType` is reset.
  // That last part is what actually restores the entry point — the mic-selector control reads
  // `expansionMicType` alone to decide Add vs Remove (ExpansionAddButton.tsx), so leaving it at
  // 'table' with an empty layout would strand the user on a "Remove" button with nothing to
  // remove. It also releases the Devio UCX exclusivity lock and the UCX hub upsize, both of which
  // key on `expansionMicType` and not on the mic count (treeFrogPorts.js,
  // treeFrogBundleMatching.js) — otherwise the design stays pinned to Android/Devio and bills a
  // larger hub for mics it no longer has.
  //
  // The reset target is `null`, NOT 'none'. Those are different states, and the difference is
  // whether the user can ever be prompted again: `null` means "dialog not yet shown"
  // (initialState.js), while 'none' means the user declined — it suppresses both recommendation
  // dialogs (they test `expansionMicType === null`) and hides the expansion type selector
  // (menus.js `userDeclined`). Nobody declined anything here; the system removed the mics because
  // the bar's coverage improved. Using 'none' would mean that when acoustics later degrade and
  // coverage IS required again, the "Expansion Microphones recommended" dialog never returns.
  // The other automatic resets already use `null` for exactly this reason — a mic-model change
  // (room.ts) and a dimension change (logic.js `resetArea`).
  //
  // Removal is announced with a toast, not a dialog: the mics are a derived consequence of a
  // value the user just changed, so blocking on a modal would read as a context jump — but
  // deleting two or three placed devices with no explanation at all reads as a bug.
  //
  // Handled HERE, before any layout work or dispatching, so the pass can be abandoned outright.
  // Everything below reads the `state` captured before this synchronous dispatch, which still
  // shows the expansion mics in place — continuing past the removal would write an equipment
  // summary billing units the design no longer has and a heatmap still painting their coverage.
  // `setExpansionMicType` is a full RECALCULATION_TRIGGER, so the dispatch schedules its own pass
  // against post-removal state; in that pass the type is null, so neither branch below can run
  // again and this cannot recurse.
  //
  // Every OTHER transition falls through to the ordinary placement pass. For TABLE that is
  // already correct once customisation is overridden (see `isExpansionCustom` further down):
  // 1 → 1 re-runs standard standalone full-path placement at the new radius (spec §2.1), 1 → 3
  // and 3 → 3 re-tile the reduced arc, and anything involving Scenario 2 keeps its existing
  // full-path behaviour (spec §7 Step 8). For CEILING the equivalents already work in
  // `computeExpansionCeilingMicCoords`: a still-present residual re-places at the new radius, and
  // an opted-in user (`expansionMicManualOptIn`) gets standalone placement over the full base box.
  const isAcousticsChange = action.type === 'container/setAcoustics';
  const isBarPrimary = MIC_CATEGORIES.BAR.has(areaState?.microphones?.model);
  const priorScenario = isAcousticsChange && areaState?.expansionMicType === 'table'
    ? consumeAcousticsPriorScenario(currentArea)
    : null;

  // Announce a removal only when mics were actually on screen to remove. Both branches can reach
  // the reset with an already-empty layout (an earlier recalc emptied it, or the placement was
  // preserved and never dispatched); resetting the type is still right there — it's the
  // bookkeeping that unsticks the Add button and the UCX lock — but "expansion microphones
  // removed" would be describing something the user never saw.
  const hadPlacedExpansionMics = (areaState?.expansionMicrophones?.layout?.length ?? 0) > 0;

  if (isAcousticsChange && isBarPrimary && areaState?.expansionMicType === 'table') {
    const currentScenario = computeTableExpansionScenario(state, areaState);
    if (isCoverageNoLongerRequired(priorScenario, currentScenario)) {
      dispatch(ReducersIndex.container.setExpansionMicType(null as any));
      if (hadPlacedExpansionMics) {
        showInfo('The bar now covers this table — expansion microphones removed.');
      }
      return;
    }
  }

  // Ceiling counterpart. Three things differ from the table branch, all deliberate:
  //
  //  1. Customised layouts are PRESERVED, not overridden. The table rule may re-place over
  //     `customized.expansionMicrophones` because tabletop mics have no supported canvas
  //     affordance, so there are no deliberate hand-set positions. Ceiling is the opposite —
  //     Add/Move/Delete are all enabled for it (actions.js conditions.add/move/delete) and
  //     click-to-place is a specified feature (ceiling spec §2.2) — so hand-placed ceiling mics
  //     are real user work and must survive the bar's coverage improving. Mirrors
  //     `isAnyDeviceCustomized`, which is what actually freezes the layout in
  //     `computeExpansionCeilingMicCoords`: a customised SPEAKER also freezes the mic layout, so
  //     checking only `customized.expansionMicrophones` here would remove mics the calc is still
  //     preserving.
  //  2. `expansionMicManualOptIn` blocks removal. It records that the user asked for expansion
  //     mics through the "Add expansion microphones" dialog when no gap existed, and
  //     `computeExpansionCeilingMicCoords` honours it by placing over the full base box. Removing
  //     them here would delete exactly what that flag says the user wants.
  //  3. No `pathLength` guard on the stash — the ceiling probe is pure geometry, never the DOM.
  if (isAcousticsChange && isBarPrimary && areaState?.expansionMicType === 'ceiling') {
    const priorOutcome = consumeAcousticsPriorCeilingOutcome(currentArea);
    const isCeilingCustom = isAnyDeviceCustomized(areaState?.customized ?? {});
    const hasOptedIn = Boolean(areaState?.expansionMicManualOptIn);
    // Both guards also mean the mics DON'T disappear: a customised layout is echoed back
    // verbatim, and an opted-in one is re-placed over the full base box. Neither should toast.
    const currentOutcome = (!isCeilingCustom && !hasOptedIn)
      ? computeCeilingExpansionOutcome(state, areaState)
      : null;

    // The gap got too SMALL to be worth a mic, rather than closing (edge case B). The placement
    // pass further down empties the layout for this — the mics vanish — so it needs the same
    // explanation the table path gives, and for the same reason: something visibly disappeared.
    //
    // Unlike the removal above this does NOT return: `expansionMicType` deliberately stays
    // 'ceiling' (the configuration is still meaningful, and a further acoustics change brings the
    // mics back), so the ordinary pass must run on to actually empty the layout.
    //
    // `priorOutcome === 'placed'` is what makes this a disappearance: from any other prior the
    // layout was already empty and nothing changed on screen.
    if (currentOutcome === 'below-min-residual' && priorOutcome === 'placed' && hadPlacedExpansionMics) {
      showInfo("This area's remaining coverage gap is too small for an expansion microphone.");
    }
    // Record the declined placement whenever it is the current outcome — not only on the
    // disappearance the toast above announces. The flag describes the state, so it must also be
    // set when the design ARRIVES here already empty (e.g. a design loaded at a below-minimum
    // tier), otherwise the mic selector would offer "Remove" for mics that were never placed.
    // In SKIP_RECALCULATION, so this dispatch does not re-enter the calculation.
    if (currentOutcome === 'below-min-residual' && !areaState?.expansionMicSuppressed) {
      dispatch(ReducersIndex.container.setExpansionMicSuppressed(true as any));
    }

    if (currentOutcome !== null
      && isCeilingCoverageNoLongerRequired(priorOutcome, currentOutcome)) {
      dispatch(ReducersIndex.container.setExpansionMicType(null as any));
      // "area" rather than "room": the base box follows the focused / full_room coverage mode,
      // so what the bar now covers may be the table's focused box, not the whole room.
      //
      // Also skipped when the prior outcome was a below-minimum intermediate — the layout was
      // already empty by then, so the reset is catching up rather than removing anything.
      if (hadPlacedExpansionMics && priorOutcome === 'placed') {
        showInfo('The bar now covers this area — expansion microphones removed.');
      }
      return;
    }
  }

  // Quick guard: skip when no device needs a plot update AND the action isn't a container change
  const needsPlotData = ['microphones', 'speakers', 'cameras'].some((key) => {
    const device = areaState[key] ?? {};
    return Object.keys(device.plotData ?? {}).length === 0;
  });
  const DEVICE_VIEWS = new Set(['microphones', 'speakers', 'acoustics', 'camera', 'ceiling']);
  const isViewToDeviceView = action.type === 'ui/setCurrentView' && DEVICE_VIEWS.has(action.payload);
  const isContainerAction = action.type.includes('container/');
  const isTableChange = action.type.includes('setTable');

  if (!needsPlotData && !isContainerAction && !isTableChange && !isViewToDeviceView) {
    return;
  }

  // Calculate full device layouts (pure selector in roomCalculations).
  // Relocation of already-placed (customised) devices to a DIFFERENT tile is only permitted
  // when the recalc is driven by a tile-size change (setCeilingTiles). Scope the flag tightly
  // around the synchronous selector call so it never leaks to other getDeviceLayouts callers.
  const isTileSizeChange = action.type.includes('setCeilingTiles');
  setTileRelocationAllowed(isTileSizeChange);
  const deviceLayoutResult = getDeviceLayouts(state);
  setTileRelocationAllowed(false);
  if (!deviceLayoutResult) return;

  const { collisions = { microphones: [], speakers: [] }, errors = [], expansionMicrophones, expansionSpeakers, ...deviceLayout } = deviceLayoutResult;

  // Determine which device types to dispatch (pure function in roomCalculations)
  const micPathExists = document.getElementById('micPath') !== null;
  const isViewChange = action.type === 'ui/setCurrentView';
  let filteredLayout = getFilteredDeviceLayout(state, deviceLayout, action, micPathExists);

  // Guard: path-based ceiling expansion mics (dcm_x on round/keystone/vshape tables) share the
  // SAME #micPath DOM-sampling placement core as the primary ceiling mic — see
  // computeExpansionCeilingMicCoords's placeViaSharedCore, which calls calculateMicPlacement with
  // model: 'tcm_x' specifically so it hits that identical branch. The PRIMARY mic already has
  // dedicated protection against this in getFilteredDeviceLayout (isPathDependentMic /
  // isViewChangeNearCeilingPathMic): when #micPath is missing from the DOM, or immediately after
  // a view change re-mounts the floorplan SVG (MuiTable's first render still uses stale
  // measurements), sampling the path collapses/clusters the computed positions instead of
  // spreading them across the table. `expansionMicrophones` bypasses that protection entirely —
  // it's destructured out of deviceLayout before getFilteredDeviceLayout ever sees it (above) —
  // so navigating to the equipment list and back could silently clobber an already-good symmetric
  // layout with a collapsed/clustered one. Preserve the existing layout under the same conditions
  // the primary mic already uses.
  const isPathBasedCeilingExpansionMic = areaState.expansionMicType === 'ceiling' &&
    ['round', 'keystone', 'vshape'].includes(areaState.table?.shape);
  const hasExistingExpansionCeilingMics = (areaState.expansionMicrophones?.layout?.length ?? 0) > 0;
  const shouldPreserveCeilingExpansionMics = isPathBasedCeilingExpansionMic &&
    hasExistingExpansionCeilingMics &&
    (!micPathExists || isViewChange);

  // The heatmap (microphones.plotData) is built INSIDE getDeviceLayouts from THIS SAME PASS's
  // freshly (and, right now, possibly DOM-timing-corrupted) computed expansion coords — it isn't
  // covered by the expansionMicrophones dispatch guard below, since it travels inside
  // `filteredLayout.microphones`, not the separate `expansionMicrophones` key. Without this, the
  // heatmap would still lose coverage circles for the corrupted mics even though the device
  // markers below stay correctly preserved. Drop just the tainted plotData so the last
  // known-good heatmap already in Redux state is left alone.
  //
  // `layout`/`qty` are dropped for the same reason: `microphones.layout` is allMicCoords, i.e.
  // the bar PLUS a mirrored copy of this pass's expansion coords. Keeping it would write the
  // corrupted/collapsed expansion count into the mirror while `expansionMicrophones.layout`
  // (guarded below) rightly keeps the good one, leaving the two records disagreeing on how many
  // expansion mics exist — the mirror then fed a wrong count to every consumer that reads it
  // (HearSpace, hover hit-testing, and previously the equipment list's Microphone locations tab).
  // Note the primary-mic guards in getFilteredDeviceLayout can't cover this: `expansionMicrophones`
  // is destructured out of `deviceLayout` before that function ever sees it, and for a vbc/abc bar
  // `isPathDependentMic` is false (BAR ∉ TABLETOP ∪ CEILING_MOUNTED) so `filteredLayout.microphones`
  // is never dropped there. The bar's own position is fixed to the wall and doesn't need this pass,
  // so preserving layout/qty costs nothing. Same surgical field-stripping the speaker navigation
  // guard uses (isViewChangeNearSpeakers in layoutFilter.ts) — plotData still refreshes for
  // everything else.
  if (shouldPreserveCeilingExpansionMics && filteredLayout.microphones) {
    const {
      plotData: _staleExpansionPlot,
      layout: _staleMirroredLayout,
      qty: _staleMirroredQty,
      ...microphonesRest
    } = filteredLayout.microphones;
    filteredLayout = { ...filteredLayout, microphones: microphonesRest };
  }

  // --- Dispatches ---

  const syncMeta: MiddlewareActionMeta = { _fromSyncMiddleware: true };
  dispatch(ReducersIndex.container.updateDevice({ ...filteredLayout, ...syncMeta } as any));

  // When speaker layout goes from empty → populated, notify HearSpace middleware
  if ((filteredLayout.speakers?.layout?.length ?? 0) > 0 && (areaState.speakers?.layout?.length ?? 0) === 0) {
    const triggerMeta: MiddlewareActionMeta = { _fromSyncMiddleware: true };
    const speakerTrigger: SpeakerPayloadMeta = { _triggerHearSpace: true };
    dispatch(ReducersIndex.container.updateDevice({ speakers: { ...speakerTrigger }, ...triggerMeta } as any));
  }

  // Dispatch expansion mic layout if calculated (bar + ceiling expansion selected). See the
  // shouldPreserveCeilingExpansionMics guard defined above (also used to protect the heatmap).
  if (expansionMicrophones && !shouldPreserveCeilingExpansionMics) {
    dispatch(ReducersIndex.container.setExpansionMicLayout(expansionMicrophones as any));
  }

  // Dispatch expansion speaker layout if calculated (bar + ceiling expansion speaker selected)
  if (expansionSpeakers) {
    dispatch(ReducersIndex.container.setExpansionSpeakerLayout(expansionSpeakers as any));

    // Soft feedback warning for AUTO-placed expansion speakers. Placement no longer DELETES
    // speakers that land within MIN_MIC_SPKR_DISTANCE_M (0.86 m) of a mic — deleting them
    // removed coverage and, with pair-enforcement, silently dropped the last pair (e.g. adding
    // an expansion mic wiped the two nearest speakers). Instead we keep the full placement and
    // warn so the user can nudge a speaker if they want. Skipped on view changes to avoid nav spam.
    if (!isViewChange) {
      const placedSpkrs = (expansionSpeakers as any)?.layout ?? [];
      const proxMics = [
        ...(((filteredLayout as any).microphones?.layout) ?? []),
        ...(((expansionMicrophones as any)?.layout) ?? []),
      ];
      if (placedSpkrs.length > 0 && proxMics.length > 0) {
        const violationCount = placedSpkrs.reduce((count: number, spkr: any) => {
          const tooClose = proxMics.some((mic: any) => {
            const dx = (spkr.location?.x ?? 0) - (mic.location?.x ?? 0);
            const dy = (spkr.location?.y ?? 0) - (mic.location?.y ?? 0);
            return Math.hypot(dx, dy) < MIN_MIC_SPKR_DISTANCE_M;
          });
          return tooClose ? count + 1 : count;
        }, 0);
        if (violationCount > 0) {
          showWarning(
            `${violationCount} expansion speaker(s) are within ${MIN_MIC_SPKR_DISTANCE_M} m of a microphone — risk of acoustic feedback.`,
            5000,
          );
        }
      }
    }
  }

  // Table expansion mic placement — runs the active `tableExpansionStrategy` (see the
  // strategy section above performDeviceCalculation). Skipped when the user has manually
  // added an expansion mic (`customized.expansionMicrophones`) so the recalculation
  // doesn't overwrite their positions — except on an acoustics change, which re-places
  // unconditionally (see `isExpansionCustom` below). NOTE: bar-covers-everything / bar-doesn't-reach do NOT
  // return `coords: []` — since PIG-513 they route to full-path standalone placement inside the
  // strategy and always yield at least one mic. Only a below-minimum-viable Scenario 3 returns
  // empty, and the empty layout is dispatched (see the second disjunct at the guard below).
  const isBarMic = MIC_CATEGORIES.BAR.has(areaState?.microphones?.model);
  const isTableExpansion = isBarMic && areaState?.expansionMicType === 'table';
  // An acoustics change re-derives the bar reach AND the TTM-X coverage radius from the new tier
  // (spec §7 Step 1 / §6), so the placed mics are answering the previous tier's question. Every
  // acoustics-driven outcome below therefore RE-PLACES rather than preserves — including over a
  // `customized.expansionMicrophones` flag.
  //
  // Overriding customisation is safe here specifically because tabletop expansion mics have no
  // supported canvas affordance: spec §2 ("there is no canvas click-to-place path for tabletop
  // mics"), and the Add/Move/Delete toolbar conditions all return false for
  // `expansionMicType === 'table'` (actions.js conditions.add/move/delete). So there are no
  // deliberate hand-set TTM-X positions to protect — unlike the ceiling case, which keeps its
  // customisation guard untouched.
  //
  // (`isAcousticsChange` and the 3 → 1 removal are handled at the top of this function, before
  // any layout work — see the transition block there.)
  const isExpansionCustom = Boolean(areaState?.customized?.expansionMicrophones) && !isAcousticsChange;
  // PIG-513: an incidental recalc must never re-place already-placed table expansion mics.
  // The microphones/speakers view-change guards live in getFilteredDeviceLayout (PIG-423/PIG-446),
  // but the table-expansion layout is dispatched here, outside that filter. The placement is
  // sampled from the table's #micPath; on transitions like equipmentlist→microphones the
  // floor-plan SVG has only just remounted, so MuiTable's first render still uses
  // tableCenter=(0,0) and sampling the path yields shifted/empty coords — the placed treefrog
  // table-expansion mics then vanish. Only an explicit change to the expansion config or the
  // surrounding geometry (table/room/acoustics/bar/reset) legitimately moves these mics; every
  // other trigger (navigation, plot/heatmap refreshes, product-family/UC-compute, undo of
  // unrelated state, …) must preserve what's already placed.
  const isExpansionPreserved = shouldPreserveExpansionMics(action.type, areaState);
  // Captured from the table-expansion re-placement below so the equipment summary (built
  // further down) reflects the same counts the canvas just got — otherwise the BOM lags a recalc.
  let refreshedExpansionSpeakers: any = null;
  let tableExpansionMicLayout: any = null;
  if (isTableExpansion && !isExpansionCustom && !isExpansionPreserved) {
    const result = tableExpansionStrategy(state, areaState);

    // Write the Coverage Density jumpPoints + value back into state so the fader can
    // read them. Marked `_fromSyncMiddleware` so the recalc trigger skips this dispatch
    // (otherwise the slider's recalc would loop). Only dispatched in the manual-add
    // table path; the other branches don't compute density.
    //
    // Preserve the user's chosen value across an INCIDENTAL recalc (navigation, heatmap refresh,
    // an unrelated device edit) so the slider doesn't jump away from where they left it. But drop
    // it when the conditions that produced it have changed — see `shouldReuseStoredDensity`. This
    // used to be a bare `jumpPoints.includes(stored.value)` membership test, which is exactly the
    // insufficient check `resolveCeilingMicDensity` warns about: Good/Great/Perfect all offer
    // jumpPoints [1, 2], so a 2 auto-computed at Good stayed "valid" at Great and Perfect and
    // masked their real default of 1. Acoustics drives the TTM-X radius and therefore the count,
    // so it is part of the density context.
    if (result.density) {
      const stored = areaState.expansionMicrophones?.coverageDensity;
      const preservedValue = shouldReuseStoredDensity(stored, result.density)
        ? stored.value
        : result.density.value;
      const sameJumpPoints = stored
        && Array.isArray(stored.jumpPoints)
        && stored.jumpPoints.length === result.density.jumpPoints.length
        && stored.jumpPoints.every((v: number, i: number) => v === result.density!.jumpPoints[i]);
      const contextUnchanged = stored?.context === result.density.context;
      const valueChanged = preservedValue !== stored?.value;
      // `contextUnchanged` joins the dirty check so the stamp itself gets persisted on the first
      // recalc after an acoustics change, even when the value and jumpPoints both happen to match.
      if (!sameJumpPoints || valueChanged || !contextUnchanged) {
        const densityMeta: MiddlewareActionMeta = { _fromSyncMiddleware: true };
        dispatch(ReducersIndex.container.setExpansionMicrophones({
          coverageDensity: { ...result.density, value: preservedValue },
          ...densityMeta,
        } as any));
      }
    }

    if (result.coords.length > 0 || (areaState?.expansionMicrophones?.layout?.length ?? 0) > 0) {
      // Second disjunct: even when the fresh calc placed 0 mics, still run this branch (with an
      // empty layout) when mics were PREVIOUSLY placed. Without this, a later acoustics/table
      // change that shrinks the residual to nothing (e.g. Perfect acoustics' large TTM-X radius
      // now covering what used to be a gap at Fair/Good) leaves the OLD mics from the earlier
      // acoustics level stuck on the canvas forever — setExpansionMicLayout is only dispatched
      // here, so a plain `if (result.coords.length > 0)` guard meant "nothing to update" and the
      // stale markers (and their now-outdated heatmap contribution) never got cleared. Mirrors
      // the identical null-vs-{qty:0} staleness fix already applied to the ceiling expansion path.
      // `setExpansionMicLayout` no longer trims for the UCC port budget (superseded by the
      // Devio UCX capacity fallback, spec §2.2/§2.3, isUCXViable — truncating a coverage-driven
      // count would under-provision the room). `tableExpansionMicLayout` — merged into
      // `stateForEquipment` further down to keep the BOM from lagging a recalc — previously had
      // to mirror the reducer's post-trim layout so the Summary tab, canvas, and Microphone
      // locations tab all agreed; now that the reducer keeps the full layout, so does this.
      tableExpansionMicLayout = { qty: result.coords.length, layout: result.coords };
      dispatch(ReducersIndex.container.setExpansionMicLayout({ qty: result.coords.length, layout: result.coords } as any));

      // Refresh microphones.plotData so the heatmap renders the same coverage gradient
      // a standalone TTM-X produces — at the algorithm's real positions, not the table-centre
      // placeholder. roomCalculations.ts builds plotData from area.expansionMicrophones.layout,
      // so we overlay the just-dispatched layout onto the captured state, re-run the selector,
      // and dispatch only the resulting plotData. _fromSyncMiddleware prevents this
      // updateDevice from re-triggering performDeviceCalculation.
      // Uses `result.coords` directly (see above — no longer port-capped) so this matches
      // exactly what was just dispatched to `setExpansionMicLayout`.
      const updatedState = {
        ...state,
        container: {
          ...state.container,
          areas: {
            ...state.container.areas,
            [currentArea]: {
              ...areaState,
              expansionMicrophones: {
                ...(areaState.expansionMicrophones ?? {}),
                layout: result.coords,
                qty: result.coords.length,
              },
            },
          },
        },
      };
      setTileRelocationAllowed(isTileSizeChange);
      const refreshed = getDeviceLayouts(updatedState);
      setTileRelocationAllowed(false);
      const refreshedPlot = refreshed?.microphones?.plotData;
      if (refreshedPlot) {
        const refreshMeta: MiddlewareActionMeta = { _fromSyncMiddleware: true };
        dispatch(ReducersIndex.container.updateDevice({
          microphones: { plotData: refreshedPlot },
          ...refreshMeta,
        } as any));
      }

      // Re-place expansion speakers against the freshly-placed table mics. The expansion
      // speaker pass inside getDeviceLayouts (dispatched above) ran before these table mic
      // positions existed, so its proximity filter (nearbyMics) only saw the bar mic and
      // could leave too many speakers (e.g. 4 instead of 2 after a ceiling→table swap).
      // `refreshed` was computed from updatedState — which now carries the table mic layout —
      // so its expansionSpeakers reflect the correct proximity-filtered count. Dispatch it
      // here so the canvas updates immediately, instead of waiting for the next recalc (which
      // is why the count only corrected itself after switching to the Speakers view).
      // setExpansionSpeakerLayout is in SKIP_RECALC_ACTIONS, so this won't loop.
      if (refreshed?.expansionSpeakers) {
        refreshedExpansionSpeakers = refreshed.expansionSpeakers;
        dispatch(ReducersIndex.container.setExpansionSpeakerLayout(refreshedExpansionSpeakers as any));
      }
    }
    if (result.coords.length === 0 && action.type === 'container/setExpansionMicType') {
      // Reaching this means the strategy placed 0 mics (coords empty). Scenario 1/2 go
      // through placeFullPath (≥1 mic) above, so 0 here is a Scenario-3 gap that's below the
      // minimum viable length after the bar→mic pull-back (spec §7 Step 4/5) — no expansion
      // mic is needed. The user explicitly chose table expansion (setExpansionMicType), so
      // surface why nothing appeared instead of leaving the "Add" a silent no-op (spec T.2).
      //
      // Wording matches the acoustics-triggered case below because it is the SAME geometry: a
      // residual too small to be worth a mic. This used to read "The bar already covers this
      // table", which contradicted the comment directly above it — the bar does not cover the
      // table here, it just leaves too little to be worth covering.
      showInfo("This table's remaining coverage gap is too small for an expansion microphone.");
    } else if (result.coords.length === 0
      && result.scenario === 3
      && isAcousticsChange
      && hadPlacedExpansionMics) {
      // The OTHER way table expansion mics vanish on an acoustics change, and the reason it needs
      // different words: this is a Scenario 3 whose residual arc is now below the minimum viable
      // length (spec §7 Step 4/5) — the gap got too SMALL to be worth a mic, it did not close.
      // `expansionMicType` deliberately stays 'table' here (unlike the 3 → 1 removal above): the
      // configuration is still meaningful, and a further acoustics change can bring the mics back.
      //
      // Three guards, each load-bearing:
      //   - `scenario === 3` — the strategy also returns `coords: []` when it could not run at
      //     all (no `#micPath` after the repaint retries, unknown bar model). That empties the
      //     layout too, but it is a DOM-timing failure, and explaining it as a geometry result
      //     would put a confident wrong reason on the disappearance.
      //   - `isAcousticsChange` — acoustics-triggered only, per the agreed scope.
      //   - `hadPlacedExpansionMics` — fires only when something visibly disappeared, not on a
      //     recalc that was already empty.
      showInfo("This table's remaining coverage gap is too small for an expansion microphone.");
    }

    // Table counterpart of the ceiling `expansionMicSuppressed` write. Set on the STATE, so it is
    // keyed on the outcome alone — no `isAcousticsChange` / `hadPlacedExpansionMics` narrowing,
    // which belong to the toast (an announcement of a change) rather than to a description of
    // where the design now stands. `scenario === 3` still guards it: a strategy that could not run
    // has decided nothing, and must not be recorded as a declined placement.
    const tableGapTooSmall = result.coords.length === 0 && result.scenario === 3;
    if (tableGapTooSmall !== Boolean(areaState?.expansionMicSuppressed)) {
      dispatch(ReducersIndex.container.setExpansionMicSuppressed(tableGapTooSmall as any));
    }
  }

  dispatch(ReducersIndex.container.setCollisions(collisions as any));

  // Toast placement warnings — suppress on view switches to avoid repeating noise.
  // Error 1200 (mic tile snapping) is filtered to the microphones view to avoid noise elsewhere.
  // Error 1500 (expansion speaker tile snapping) currently fires in any view — PIG-382 should
  // add inSpeakersView filtering here once the speakers view is defined.
  if (!isViewChange && errors?.length > 0) {
    const inMicrophonesView = state.ui?.currentView === 'microphones';
    errors.forEach((error: any) => {
      if (error.msg && error.reset) {
        if (error.code === 1200) {
          if (inMicrophonesView) showWarning(error.msg);
        } else {
          showWarning(error.msg);
        }
      }
    });
  }

  // Equipment list: use the updated layout for container/ actions, raw state for view switches
  // (view switches must not override saved quantities with recalculated ones)
  let stateForEquipment = isViewChange ? state : buildEquipmentState(state, filteredLayout);
  // buildEquipmentState only folds in microphones/speakers/cameras. When the table-expansion
  // pass above re-placed the expansion mics and/or speakers, merge those into the area too so
  // the BOM count matches the canvas in the same recalc (otherwise the summary lags one pass).
  if (!isViewChange && (tableExpansionMicLayout || refreshedExpansionSpeakers)) {
    const eqArea = stateForEquipment.container.areas[currentArea];
    stateForEquipment = {
      ...stateForEquipment,
      container: {
        ...stateForEquipment.container,
        areas: {
          ...stateForEquipment.container.areas,
          [currentArea]: {
            ...eqArea,
            ...(tableExpansionMicLayout && {
              expansionMicrophones: { ...eqArea.expansionMicrophones, ...tableExpansionMicLayout },
            }),
            ...(refreshedExpansionSpeakers && {
              expansionSpeakers: { ...eqArea.expansionSpeakers, ...refreshedExpansionSpeakers },
            }),
          },
        },
      },
    };
  }
  // The updateDevice dispatch above may have re-resolved Platform / Product Family / UC Compute
  // (see `DeviceCalculationOptions.getState`). Without this, switching speakers back to C-IC6
  // restored Devio UCX in state but this pass still listed the Tesira fallback's DSP.
  const latestArea = !isViewChange ? options.getState?.()?.container?.areas?.[currentArea] : null;
  if (latestArea) {
    const eqArea = stateForEquipment.container.areas[currentArea];
    stateForEquipment = {
      ...stateForEquipment,
      container: {
        ...stateForEquipment.container,
        areas: {
          ...stateForEquipment.container.areas,
          [currentArea]: {
            ...eqArea,
            platform: latestArea.platform,
            productFamily: latestArea.productFamily,
            ucCompute: latestArea.ucCompute,
          },
        },
      },
    };
  }
  dispatch(ReducersIndex.container.setEquipmentList(getEquipmentSummary(stateForEquipment)));
  if (!getIsLoadingDesign() && !isViewChange) {
    dispatch(ReducersIndex.uiReducers.setNeedsResave(true));
  }

  // Tree Frog peripheral-port overflow. The amp count is silently capped in the
  // equipment calc to keep the deployed config port-legal; this is where the user
  // hears about it. Only fires for Devio designs where speakers demand more amp
  // ports than the budget can spare. View switches don't change the underlying
  // config so we skip the toast on those to avoid spurious noise.
  // Read-and-clear: the flag is set mid-pass by the nested dispatch that tripped the Case 3b
  // switch (see `suppressNextPortOverflowToast`), so it must be consumed here, at the one place
  // it applies, rather than left standing for whatever pass happens to run next.
  const suppressPortOverflowToast = options.suppressPortOverflowToast || suppressNextPortOverflowToast;
  suppressNextPortOverflowToast = false;
  if (!isViewChange && !suppressPortOverflowToast) {
    const portOverflow = checkPortOverflow(stateForEquipment.container?.areas?.[stateForEquipment.container?.activeTab]);
    if (portOverflow) {
      showWarning(portOverflow.message, 6000);
    }
  }

  const hasMicCollisions = (collisions?.microphones?.length ?? 0) > 0;
  dispatch(ReducersIndex.uiReducers.setShowOverlaps(hasMicCollisions || (collisions?.speakers?.length ?? 0) > 0));
  if (hasMicCollisions) {
    showWarning('Microphone coverage overlaps detected. Consider adjusting device positions for optimal audio coverage.', 5000);
  }

  const constants = toolIndex[state.ui?.tool]?.constants;
  const currentViewState = state.ui?.currentView;
  const deviceIsMoving = state.ui.deviceIsMoving;
  const hasErrors = state.ui.error && Object.keys(state.ui.error).length > 0;
  const plotDevices = constants?.devices ?? ['microphones', 'speakers', 'cameras'];
  const shouldShowHeatmap = [...plotDevices, 'acoustics'].includes(currentViewState) && !deviceIsMoving && !hasErrors;

  dispatch(ReducersIndex.uiReducers.setShowDevices(!hasErrors));
  if (shouldShowHeatmap) dispatch(ReducersIndex.uiReducers.setShowHeatmap(true));

  if (state.container._toastMessage) {
    // Mirror toastMiddleware's severity dispatch — this path runs first during
    // recalc-driven actions and clears the message before toastMiddleware sees
    // it. Without this branching, every reducer-set toast surfaces as info-blue
    // regardless of whether the reducer set _toastSeverity = 'warning' / 'error'.
    const severity = state.container._toastSeverity;
    const show =
      severity === 'warning' ? showWarning :
      severity === 'error'   ? showError   :
      severity === 'success' ? showSuccess :
                               showInfo;
    show(state.container._toastMessage, 3000);
    dispatch(ReducersIndex.container.clearToastMessage());
  }
};

/**
 * Main middleware function
 */
const calculateDevicesMiddleware: Middleware = ({ dispatch, getState }: Store) => next => (action: AnyAction) => {
  const preState = getState();
  
  // Debug logging (disabled for performance)
  // if (action.type.includes('container/') || action.type.includes('ui/')) {
  //   console.log('🔄 SYNC ACTION:', action.type, {
  //     willRecalc: shouldRecalculate(action),
  //     payload: action.payload
  //   });
  // }
  
  // Early exit for blocked actions
  if (willRemoveDeviceBeBlocked(preState, action)) {
    // Pass to error middleware and exit
    return next(action);
  }
  
  
  // Check if local calculation is enabled
  const constants = toolIndex[preState.ui?.tool]?.constants;
  const localCalc = constants?.tool?.localCalc ?? false;
  const areaDefined = !isEmpty(getBoundingBox(preState));
  
  if (!localCalc || !areaDefined) {
    return next(action); // Not our responsibility
  }

  // Warn-and-confirm intercept for dimension changes that would clear manual edits.
  // When the user has any `customized.*` flag set (mics, speakers, cameras, expansion
  // mics, expansion speakers) AND dispatches `setDimensions`, we pause the action, park
  // it in `ui.pendingParameterChangeAction`, and open the `confirmManualEditReset`
  // modal. The modal re-dispatches the original action with `meta._confirmedReset: true`
  // on user confirmation; that flag short-circuits this check so the action passes
  // through normally.
  //
  // The bypass flag lives on `action.meta` rather than `action.payload` so it stays
  // invisible to reducers — useful for transport-only markers like this.
  if (action.type === CONFIRM_INTERCEPT_ACTION && !(action as any).meta?._confirmedReset) {
    const confirmArea = preState.container?.areas?.[preState.container?.activeTab];
    const confirmCustomized = confirmArea?.customized ?? {};
    const hasManualEdits = Boolean(
      confirmCustomized.microphones
      || confirmCustomized.speakers
      || confirmCustomized.cameras
      || confirmCustomized.expansionMicrophones
      || confirmCustomized.expansionSpeakers,
    );
    if (hasManualEdits) {
      dispatch(ReducersIndex.uiReducers.setPendingParameterChangeAction({
        type: action.type,
        payload: action.payload,
      }));
      dispatch(ReducersIndex.uiReducers.openModal('confirmManualEditReset'));
      return; // Block the original action — modal will re-dispatch on confirm.
    }
  }

  // Special handling for specific action types BEFORE the action is processed
  const currentArea = preState.container?.activeTab;
  const areaState = preState.container?.areas[currentArea];
  
  if (areaState) {
    switch (true) {
      // Handle view changes - hide camera when switching to non-device views
      case action.type === 'ui/setCurrentView':
        // Skip camera hiding during reset operations to prevent interference with undo/reset
        const isResettingOrUndoing = false; // We'll determine this from state if needed
        
        if (!isResettingOrUndoing) {
          // Don't hide cameras by changing model - this breaks equipment list
          // Camera visibility should be controlled by UI layer, not by changing data model

        }
        break;

      // Handle device customization
      case action.type.includes('addDevice'):
      case action.type.includes('removeDevice'):
      case action.type.includes('moveDevice'):
        const currentDevice = preState.ui.currentDevice || preState.container.currentDevice;

        // Manually adding/removing/moving a mic or speaker invalidates the focused/full_room
        // preset. The setCustomized dispatch below flips customized.microphones/speakers to
        // true, and — because that's itself a RECALCULATION_TRIGGER — re-enters this
        // middleware synchronously and runs getDeviceLayouts BEFORE the real addDevice/
        // removeDevice/moveDevice reducer ever executes. Once customized, getDeviceLayouts
        // always resets coverageDensity to its box-appropriate default (see
        // clearPrimaryCoverage in room.ts), so by the time the reducer's own stash logic
        // runs afterward, the user's original slider value is already gone — Undo would
        // then restore that already-defaulted density instead. Stash it HERE, before
        // setCustomized can trigger that recalculation, guarded to only fire on the first
        // edit (mirrors clearPrimaryCoverage) so a later add/remove/move doesn't clobber the
        // original with an already-defaulted value. `_fromSyncMiddleware: true` marks this
        // as a sync-internal update so it doesn't itself trigger another recalculation pass.
        if (currentDevice === 'microphones' || currentDevice === 'speakers') {
          const deviceState = areaState[currentDevice];
          if (deviceState && !deviceState.hideCoverageToggle) {
            dispatch(ReducersIndex.container.updateDevice({
              [currentDevice]: {
                hideCoverageToggle: true,
                ...(deviceState.coverageDensity && { lastCoverageDensity: deviceState.coverageDensity }),
              },
              _fromSyncMiddleware: true,
            }));
          }
        }

        // For addDevice/moveDevice, always set the device as customized
        // This ensures the filteredLayout logic below handles device updates correctly
        if ((action.type.includes('moveDevice') || action.type.includes('addDevice')) && currentDevice) {
          dispatch(ReducersIndex.container.setCustomized({
            [currentDevice]: [] // Use empty array like speakers use
          }));
        } else if (action.type.includes('removeDevice')) {
          // For removeDevice, use the customSettings if available
          const customSettings = constants?.customSettings?.[currentDevice];
          if (customSettings) {
            dispatch(ReducersIndex.container.setCustomized({
              [currentDevice]: customSettings
            }));
          }
        }
        break;
        
      // Clear customizations on dimension changes
      case action.type.includes('setDimensions'):
      case action.type.includes('setCeilingHeight'):
        const { customized = {} } = areaState;

        // Clear customized flags for any customized devices
        const resetCustom: any = {};
        if (customized.microphones) resetCustom.microphones = [];
        if (customized.speakers) resetCustom.speakers = [];
        if (customized.cameras) resetCustom.cameras = [];

        // If any devices were customized, clear their flags
        if (Object.keys(resetCustom).length > 0) {
          dispatch(ReducersIndex.container.setCustomized(resetCustom));

          // Only show warning if the customized devices actually have devices placed
          const devicesWithLayout = [];
          if (customized.microphones && areaState.microphones?.layout?.length > 0) {
            devicesWithLayout.push('microphones');
          }
          if (customized.speakers && areaState.speakers?.layout?.length > 0) {
            devicesWithLayout.push('speakers');
          }
          if (customized.cameras && areaState.cameras?.layout?.length > 0) {
            devicesWithLayout.push('cameras');
          }

          // Only show warning if there are actual devices to reset
          if (devicesWithLayout.length > 0) {
            showWarning(`Changing room dimensions will reset ${devicesWithLayout.join(' and ')} to the recommended solution.`);
          }
        }
        
        // Trigger microphone validation when ceiling height changes
        if (action.type.includes('setCeilingHeight')) {
          setTimeout(() => {
            const newState = getState();
            const currentArea = newState.container.activeTab;
            const microphones = newState.container?.areas?.[currentArea]?.microphones;
            
            // Only run validation if we have ceiling microphones and are using CRD
            if (newState.container.currentTool === 'conferenceRoomDesigner' &&
                microphones && microphones.model === 'ceilingmic') {
              validate.microphones(newState, dispatch);
            }
          }, 100); // Slightly longer delay to ensure state is fully updated
        }
        break;

      // PIG-402: When the user selects "Ceiling Tile" mic type, the type selector always
      // dispatches bma360d_24 (the representative key). Pre-select the correct variant based
      // on ceiling tile config and unit system before the action reaches the reducer.
      // Rules per PIG-294 AC1:
      //   imperial tile (2x2 / 2x4 / 4x2) → bma360d_24
      //   600mm tile                       → bma360d_600
      //   no tiles + imperial room         → bma360d_24
      //   no tiles + metric room           → bma360d_600
      case action.type.includes('setMicrophones') &&
           action.payload?.model === 'bma360d_24' &&
           !action.payload?._fromSyncMiddleware: {
        const isMicModelSwitch = 'model' in (action.payload ?? {});
        if (isMicModelSwitch) {
          const tileKey = areaState?.ceilingTiles?.tile ?? 'no_ceiling_tiles';
          const isMetric = getIsMeter(preState);
          const preselected = constants.preselectBma360dModel(tileKey, isMetric);
          if (preselected !== action.payload.model) {
            action.payload = {
              ...action.payload,
              model: preselected,
              modelName: constants.bmaModelNames[preselected],
            };
          }
        }
        break;
      }

      // PIG-420: When ceiling tiles change while a BMA360D is selected, auto-correct
      // the variant to match the new tile (or the unit-system default when tiles are
      // cleared). The user has no manual variant override under the current design,
      // so this dispatch is silent. Reuses preselectBma360dModel + bmaModelNames so
      // the rule is consistent with the type-pick logic above.
      case action.type.includes('setCeilingTiles') &&
           !action.payload?._fromSyncMiddleware: {
        const currentMicModel = areaState?.microphones?.model;
        if (MIC_CATEGORIES.CEILING_TILE.has(currentMicModel)) {
          const isMetric = getIsMeter(preState);
          const targetModel = constants.preselectBma360dModel(action.payload?.tile, isMetric);
          if (targetModel !== currentMicModel) {
            dispatch(ReducersIndex.container.setMicrophones({
              model: targetModel,
              modelName: constants.bmaModelNames[targetModel],
              _fromSyncMiddleware: true,
            } as any));
          }
        }
        break;
      }

      // PIG-420 / PIG-294 AC6: When the room unit system changes and no ceiling tiles
      // are defined, re-evaluate the BMA360D variant so the SKU shown in the equipment
      // list matches the new unit system. (When tiles are defined the tile drives the
      // variant — AC4 — so a unit change is a no-op in that branch of preselect.)
      // preState reflects the old units; action.payload is the new unit value.
      case action.type === 'ui/setUnits' &&
           !action.payload?._fromSyncMiddleware: {
        const currentMicModel = areaState?.microphones?.model;
        if (MIC_CATEGORIES.CEILING_TILE.has(currentMicModel)) {
          const tileKey = areaState?.ceilingTiles?.tile ?? 'no_ceiling_tiles';
          const isMetricAfter = action.payload === 'meters';
          const targetModel = constants.preselectBma360dModel(tileKey, isMetricAfter);
          if (targetModel !== currentMicModel) {
            dispatch(ReducersIndex.container.setMicrophones({
              model: targetModel,
              modelName: constants.bmaModelNames[targetModel],
              _fromSyncMiddleware: true,
            } as any));
          }
        }
        break;
      }


      // Handle table changes - reset customized devices and force recalculation
      case action.type.includes('setTable'):
        // Handle table shape changes - update microphone coverage
        if ('shape' in action.payload) {
          const newShape = Boolean(action.payload.shape);
          const currentMics = areaState?.microphones;

          // Update microphone coverage based on table presence
          // Only update the coverage property to preserve custom layouts
          if (currentMics) {
            dispatch(ReducersIndex.container.setMicrophones({
              coverage: { full_room: !newShape, focused: newShape }
            } as any));
          }

          // When table is removed, reset camera to initial position (same as undo button)
          if (!newShape) {
            dispatch(ReducersIndex.container.resetArea('cameras') as any);
          }
        }
        
        // Handle table movement/size changes - but NOT initial table creation
        if (('transform' in action.payload || 'size' in action.payload) && !('shape' in action.payload)) {
          // Only clear customizations for actual table movement/resize, not initial table creation
          // If 'shape' is also in the payload, this is initial table creation, not movement
          const { customized: tableCustomized = {} } = areaState;
          
          // Clear device customization flags when table moves or resizes
          const resetCustomized: any = {};
          if (tableCustomized.microphones) resetCustomized.microphones = [];
          if (tableCustomized.speakers) resetCustomized.speakers = [];
          if (tableCustomized.cameras) resetCustomized.cameras = [];

          // If any devices were customized, clear their flags
          if (Object.keys(resetCustomized).length > 0) {
            dispatch(ReducersIndex.container.setCustomized(resetCustomized));

            // Only show warning if the customized devices actually have devices placed
            const devicesWithLayout = [];
            if (tableCustomized.microphones && areaState.microphones?.layout?.length > 0) {
              devicesWithLayout.push('microphones');
            }
            if (tableCustomized.speakers && areaState.speakers?.layout?.length > 0) {
              devicesWithLayout.push('speakers');
            }
            if (tableCustomized.cameras && areaState.cameras?.layout?.length > 0) {
              devicesWithLayout.push('cameras');
            }

            // Only show warning if there are actual devices to reset
            if (devicesWithLayout.length > 0) {
              showWarning(`Moving or changing the table will reset ${devicesWithLayout.join(' and ')} to the recommended solution.`);
            }
          }
        }
        
        // Ensure microphones have focused coverage when table exists (for any table change).
        // Use the NEW shape from payload (not pre-action state) so removing the table doesn't
        // override the full_room coverage that was just dispatched above.
        const nextTableShape = 'shape' in action.payload ? action.payload.shape : areaState?.table?.shape;
        const hasTableAfterChange = Boolean(nextTableShape);
        if (hasTableAfterChange && areaState?.microphones) {
          // Only update coverage property to preserve custom layouts
          dispatch(ReducersIndex.container.setMicrophones({
            coverage: { full_room: false, focused: true }
          } as any));
        }
        
        // Clear plot data to force device recalculation for any table change
        // Include _fromSyncMiddleware to prevent marking devices as customized
        // Use _forceSpeakerRecalc to trigger immediate recalculation without debouncing
        if ('transform' in action.payload || 'size' in action.payload || 'shape' in action.payload) {
          const tableMeta: MiddlewareActionMeta = { _fromSyncMiddleware: true };
          const tableSpeakerMeta: SpeakerPayloadMeta = { _forceSpeakerRecalc: true };
          dispatch(ReducersIndex.container.updateDevice({
            microphones: { plotData: {} },
            speakers: { plotData: {}, ...tableSpeakerMeta },
            cameras: { plotData: {} },
            ...tableMeta,
          } as any));
        }
        break;
    }
  }
  
  // Sample the table-expansion scenario BEFORE the acoustics change is reduced and React
  // repaints #micPath (see `pendingAcousticsPriorScenario`). Both the bar reach and the path's
  // device-to-edge inset are keyed on the acoustics tier, so this is the last moment at which
  // the OUTGOING tier's scenario is observable — the placement pass that needs it runs a couple
  // of animation frames later, against the new geometry.
  if (action.type === 'container/setAcoustics') {
    const isBarExpansionArea = MIC_CATEGORIES.BAR.has(areaState?.microphones?.model);
    const isTableExpansionArea = isBarExpansionArea && areaState?.expansionMicType === 'table';
    const isCeilingExpansionArea = isBarExpansionArea && areaState?.expansionMicType === 'ceiling';
    const probePath = document.getElementById('micPath') as unknown as SVGPathElement | null;
    setAcousticsPriorScenario({
      areaId: currentArea,
      scenario: isTableExpansionArea ? computeTableExpansionScenario(preState, areaState) : null,
      ceilingOutcome: isCeilingExpansionArea ? computeCeilingExpansionOutcome(preState, areaState) : null,
      pathLength: probePath ? probePath.getTotalLength() : null,
    });
  }

  // Process the action
  const result = next(action);

  // A Devio UCX capacity fallback (`applyUcxCapacityFallback` in room.ts) may have just silently
  // promoted a ceiling EXPANSION mic group to the PRIMARY mic (bar removed) — `promoteExpansionToPrimary`
  // commits an ESTIMATED qty but an EMPTY layout there, because computing exact pixel positions
  // needs the real `#micPath` DOM element `calculateMicPlacement` reads, which isn't available
  // synchronously inside a reducer. Re-dispatch `setMicrophones` with the SAME model/modelName the
  // promotion just committed — exactly what a manual Bar→Ceiling switch sends — so this
  // middleware's own needsDOMUpdate/RECALCULATION_TRIGGER handling below runs the real, DOM-based
  // placement pass for it, same as any other primary mic type change gets.
  {
    const postPromoteState = getState();
    // The flag lives on the CONTAINER slice itself (`state._pendingCeilingPromotionRecalc` in
    // room.ts, set alongside `state._toastMessage` — same transient-signal convention, same
    // placement), not on the area.
    if (postPromoteState.container?._pendingCeilingPromotionRecalc) {
      const promotedAreaTab = postPromoteState.container?.activeTab;
      const promotedArea = postPromoteState.container?.areas?.[promotedAreaTab];
      dispatch(ReducersIndex.container.clearPendingCeilingPromotionRecalc());
      dispatch(ReducersIndex.container.setMicrophones({
        model: promotedArea?.microphones?.model,
        modelName: promotedArea?.microphones?.modelName,
      } as any));
    }
  }

  // The same capacity fallback's Case 3b (`attemptTableToCeilingSwitch` in room.ts) may instead
  // have just switched an over-capacity bar + TABLE expansion design to CEILING expansion. It
  // seeds a real (renderable) predicted layout so the mics never vanish, but that seed is the
  // grid prediction — the path-based table shapes (round/keystone/vshape) and the tile-snapping
  // pass still need the real placement.
  //
  // The switch usually rides in on this middleware's OWN `setExpansionMicLayout` /
  // `setExpansionSpeakerLayout` commit, and both are in SKIP_RECALCULATION — so without this the
  // design sat on Ceiling and never got a placement pass at all. Rather than re-dispatching
  // `setExpansionMicType('ceiling')` (what a manual switch sends), this forces the deferred,
  // DOM-based pass for the CURRENT action: re-running that reducer would also re-run its
  // acoustics-limited-height toast, which lands in `_toastMessage` moments after
  // `toastMiddleware` consumed Case 3b's own "…switched to ceiling expansion microphones" notice
  // and visibly displaces it. Forcing the pass here gets the placement with no reducer
  // side-effects and no second toast.
  let forceExpansionCeilingRecalc = false;
  {
    const postSwitchState = getState();
    if (postSwitchState.container?._pendingExpansionCeilingRecalc) {
      forceExpansionCeilingRecalc = true;
      // The pass that is mid-flight around this very dispatch must not warn about the tabletop
      // configuration the switch just discarded — see `suppressNextPortOverflowToast`.
      suppressNextPortOverflowToast = true;
      dispatch(ReducersIndex.container.clearPendingExpansionCeilingRecalc());
    }
  }

  // Populate the equipment list whenever it would otherwise be stale or empty:
  // - setProjectData: area data just restored from server; equipmentList reset to []
  // - setCurrentView('equipmentlist'): user opened the tab; list may never have been computed
  // Neither case needs a full device layout recalculation — getEquipmentSummary reads qty/model
  // directly from state and is cheap to call.
  if (action.type === 'container/setProjectData') {
    const postState = getState();
    const constants = toolIndex[postState.ui?.tool]?.constants;
    if (constants?.tool?.localCalc) {
      dispatch(ReducersIndex.container.setEquipmentList(getEquipmentSummary(postState)));
    }
    return result;
  }

  // When opening the equipment list, ensure device layouts (including cameras) are populated so
  // positioning tabs have data. performDeviceCalculation reads state.ui.currentView to decide
  // whether to skip cameras — 'equipmentlist' is in hideDevices so cameras would be excluded.
  // Override currentView to 'camera' in the state we pass so all devices are calculated.
  if (action.type === 'ui/setCurrentView' && action.payload === 'equipmentlist') {
    const postState = getState();
    const constants = toolIndex[postState.ui?.tool]?.constants;
    if (constants?.tool?.localCalc && !isEmpty(getBoundingBox(postState))) {
      dispatch(ReducersIndex.container.setEquipmentList(getEquipmentSummary(postState)));
      const stateWithCameraView = { ...postState, ui: { ...postState.ui, currentView: 'camera' } };
      performDeviceCalculation(dispatch, stateWithCameraView, action);
    }
    return result;
  }

  // Check if we should recalculate AFTER the action. `forceExpansionCeilingRecalc` overrides
  // SKIP_RECALCULATION for the one dispatch that just switched the design to ceiling expansion
  // from inside a reducer (see the block above) — that layout commit is normally, and rightly,
  // skipped, but this time the state it landed in needs a placement pass.
  if (shouldRecalculate(action) || forceExpansionCeilingRecalc) {
    const postState = getState();

    // Don't recalculate when unlinking speakers - positions haven't changed
    // Only recalculate when linking (which resets to default positions)
    if (action.type === 'container/toggleSpeakerLink') {
      const currentAreaTab = postState.container?.activeTab;
      const area = postState.container?.areas?.[currentAreaTab];
      const isNowLinked = area?.speakers?.linkedSpeakers;

      // If now unlinked, skip recalculation (positions stayed the same)
      if (!isNowLinked) {
        return result;
      }
    }

    // Don't recalculate if there's a blocking error
    if (postState.ui?.error?.['130']) {
      return result;
    }

    // Only recalculate for view changes that show device plots.
    // Non-device views (floorplan, furniture, equipmentlist, …) don't need a recalc.
    const DEVICE_VIEWS = new Set(['microphones', 'speakers', 'acoustics', 'camera', 'ceiling']);
    if (action.type === 'ui/setCurrentView' && !DEVICE_VIEWS.has(action.payload)) {
      return result;
    }

    // For dimension, ceiling height, table, and microphone changes, defer calculation to allow DOM to update.
    // Also defer for ttm_x (table mic) when switching from a hideDevices view (e.g. equipmentlist) to any
    // device view — the hidden views unmount the floor plan SVG, so micPath is absent from the DOM
    // until React re-renders. Running calculateMicPlacement against a null micPath collapses the
    // mic count to a degenerate value.
    const activeTab = postState.container?.activeTab;
    const micModel = postState.container?.areas[activeTab]?.microphones?.model;
    const hideDevices: string[] = constants?.hideDevices ?? [];
    const cameFromHiddenView = hideDevices.includes(preState.ui?.currentView);
    const isViewChangeToDeviceView = action.type === 'ui/setCurrentView' && DEVICE_VIEWS.has(action.payload);
    // Bar-expansion ceiling/pendant speakers on a round/keystone/vshape table sample #spkrPath the
    // same way ttm_x table mics sample #micPath below — so a view change away from a hidden view
    // (equipmentlist unmounts the floor plan SVG entirely) needs the same repaint-wait treatment.
    // Confirmed via repro: right after such a view change, #spkrPath can exist with a POSITIVE but
    // transiently wrong (too-short) length while the canvas panel is still resizing — collapsing the
    // sampled speaker count/positions — not just the "element missing" case the fallback inside
    // calculateSpeakerPlacement already covers.
    const expansionAreaState = postState.container?.areas[activeTab];
    const hasPathBasedExpansionSpeaker = ['ceiling', 'pendant'].includes(expansionAreaState?.expansionSpeakerType) &&
      ['round', 'keystone', 'vshape'].includes(expansionAreaState?.table?.shape);
    const needsDOMUpdate = forceExpansionCeilingRecalc ||
                           // The ceiling expansion mics this pass exists to place sample #micPath
                           // on the path-based table shapes, exactly like the
                           // `setExpansionMicType` trigger below — so it needs the same
                           // wait-for-repaint treatment rather than an immediate pass.
                           action.type.includes('setDimensions') ||
                           action.type.includes('setCeilingHeight') ||
                           action.type.includes('setTable') ||
                           action.type.includes('setMicrophones') ||
                           // MuiTable's micPath/spkrPath both derive their device-to-edge inset from
                           // the acoustics tier (MuiTableConstants.deviceToEdge, indexed by acoustics),
                           // so an acoustics change reshapes both paths exactly like a table/dimension
                           // change does. Without waiting here, calculateSpeakerPlacement/
                           // calculateMicPlacement sample the PREVIOUS tier's path (React hasn't
                           // repainted #micPath/#spkrPath yet), so bar-expansion ceiling/pendant mic
                           // and speaker positions land one acoustics-switch behind — correct only
                           // after a LATER, unrelated recalculation happens to catch up.
                           action.type === 'container/setAcoustics' ||
                           // Switching to 'table' expansion re-renders the table mic path with
                           // ttm_x parameters (see MuiTable.tsx). The middleware must wait for
                           // React to repaint the new path before sampling it in
                           // calcExpansionTableMicPlacement — otherwise we sample the previous
                           // (bar-shaped or degenerate) path and the mic count is wrong.
                           action.type === 'container/setExpansionMicType' ||
                           // setExpansionMicrophones (expansion mic height field, coverage-density
                           // slider) and setExpansionMicCoverage (focused/full-room toggle) both feed
                           // calculateMicPlacement's round/keystone/vshape #micPath sampling for the
                           // ceiling expansion mic. Without waiting here too, a recalc firing before
                           // React has (re)painted #micPath falls back to the flat single-row grid
                           // (calculateCoordinates with qty.y=1) instead of tracing the table's
                           // taper — every expansion mic lands on one straight line at the box's
                           // vertical center instead of split per side + one near the table's point.
                           action.type === 'container/setExpansionMicrophones' ||
                           action.type === 'container/setExpansionMicCoverage' ||
                           (isViewChangeToDeviceView && micModel === 'ttm_x' &&
                             (action.payload === 'microphones' || cameFromHiddenView)) ||
                           (isViewChangeToDeviceView && hasPathBasedExpansionSpeaker &&
                             (action.payload === 'speakers' || action.payload === 'microphones' || cameFromHiddenView));

    if (needsDOMUpdate) {
      // Claim this pass as the latest one scheduled — see `latestDeferredCalculationToken`'s
      // doc comment above for why: any EARLIER pass still polling when this dispatch happens
      // must recognize it has been superseded and bail out instead of running to completion
      // against a stale `action` paired with state this newer pass is about to move past.
      const myCalculationToken = ++latestDeferredCalculationToken;

      // Use requestAnimationFrame with multiple attempts to ensure React has completed its render cycle
      // React's useLayoutEffect and useMemo need time to recalculate and render the micPath element
      let attempts = 0;
      const maxAttempts = 10;
      const retryDelay = 20; // ms between retries (after first RAF)
      // A path can EXIST with a positive length that's still wrong — e.g. right after a view change
      // swaps the canvas panel size, #spkrPath/#micPath can report a genuinely shorter length for a
      // frame or two while the SVG viewport is still resizing, before settling on its real geometry.
      // That race is specific to VIEW CHANGES (isViewChangeToDeviceView) — the canvas panel itself is
      // being resized/remounted. The other needsDOMUpdate triggers (setTable, setMicrophones,
      // setAcoustics, setExpansionMicType, etc.) don't resize the canvas; the double-RAF wait above is
      // already enough for React to repaint the path with the new geometry, and "positive length" was
      // always sufficient for those — requiring a second stable reading for every trigger only adds an
      // extra setTimeout tick with no benefit, and breaks single-attempt assumptions (tests and any
      // synchronous consumers) for the triggers that never had this problem. So only demand two
      // consecutive matching readings when the view-change canvas-resize race is actually in play;
      // everything else keeps the original "exists with positive length" check. Tracked per-path so
      // either one stabilizing is enough (round/keystone/vshape mic and speaker paths update together).
      const requiresStableLength = isViewChangeToDeviceView;
      let lastMicLength: number | null = null;
      let lastSpkrLength: number | null = null;

      const attemptCalculation = () => {
        // Superseded by a newer needsDOMUpdate pass scheduled after this one (e.g. another
        // recalc-triggering action fired while this poll was still waiting on rAF/#micPath).
        // That newer pass owns this area's recalculation from here on — running this stale
        // one too would pair its original (now outdated) `action` with whatever state exists
        // by the time it finally gets to run, producing a redundant/incorrect second pass.
        if (myCalculationToken !== latestDeferredCalculationToken) {
          return;
        }

        attempts++;
        const latestState = getState();

        // Check again for blocking errors
        if (latestState.ui?.error?.['130']) {
          return;
        }

        // Hold canvas update while the expansion mic dialog is awaiting user response
        const expansionMicPending = latestState.ui?.modals?.some(
          (m: any) => m.modalName === 'expansionMicRecommendation' && m.modalOpen
        );
        if (expansionMicPending) {
          return;
        }

        // Check if micPath/spkrPath exist and have a non-zero length — for view changes, require it
        // to match the previous attempt's reading too (see comment above on why "positive" alone isn't
        // sufficient during a canvas resize).
        const micPath = document.getElementById('micPath') as unknown as SVGPathElement | null;
        const spkrPath = document.getElementById('spkrPath') as unknown as SVGPathElement | null;
        const micLength = micPath ? micPath.getTotalLength() : 0;
        const spkrLength = spkrPath ? spkrPath.getTotalLength() : 0;
        const pathHasLength = requiresStableLength
          ? ((micLength > 0 && micLength === lastMicLength) || (spkrLength > 0 && spkrLength === lastSpkrLength))
          : (micLength > 0 || spkrLength > 0);
        lastMicLength = micLength;
        lastSpkrLength = spkrLength;

        if (pathHasLength || attempts >= maxAttempts) {
          // Either a path is ready (settled) or we've tried enough times
          performDeviceCalculation(dispatch, latestState, action, {
            suppressPortOverflowToast: forceExpansionCeilingRecalc,
            getState,
          });
        } else {
          // Path not ready yet, try again after a short delay
          setTimeout(attemptCalculation, retryDelay);
        }
      };

      // Use requestAnimationFrame for first attempt - waits for next browser paint (~16ms)
      // This is faster and more aligned with React's render cycle than arbitrary setTimeout
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Double RAF ensures DOM has been painted
          attemptCalculation();
        });
      });
    } else {
      // Other actions can calculate immediately. Also claims the latest-pass token (see
      // `latestDeferredCalculationToken`'s doc comment) so an earlier action's still-pending
      // deferred poll recognizes it has been superseded by THIS pass instead of running
      // its own stale one afterward.
      ++latestDeferredCalculationToken;
      performDeviceCalculation(dispatch, postState, action, {
        suppressPortOverflowToast: forceExpansionCeilingRecalc,
        getState,
      });
    }
  }

  // Bar mic coverage gap detection — CRD only, runs synchronously after the action.
  // setTable is included because adding/removing/resizing the table changes the focused
  // baseBox geometry (and, when no table → table, flips the default coverage mode), so
  // the gap calc and the secondary-selector visibility must re-evaluate.
  const BAR_GAP_TRIGGERS = [
    'container/setMicrophones',
    'container/setAcoustics',
    'container/setDimensions',
    'container/setTable',
    'container/resetArea',
    // The acoustics-driven automatic removal resets `expansionMicType` from a RAF-deferred
    // `performDeviceCalculation`, which lands AFTER this detector has already run for that same
    // `setAcoustics` — and it ran while the type was still 'table'/'ceiling'. Without this entry
    // nothing re-evaluates the gap against post-removal state, so the design is left holding
    // whatever `expansionMicRecommended` the pre-removal pass computed until the next unrelated
    // trigger. Including the action here keeps the flag truthful straight away.
    //
    // It cannot cause a spurious prompt: the dialog below additionally requires
    // `expansionMicType === null`, and the user-driven dispatches of this action all set a
    // concrete type ('ceiling' / 'table' / 'none').
    'container/setExpansionMicType',
  ];
  if (localCalc && areaDefined && BAR_GAP_TRIGGERS.some(t => action.type === t)) {
    const gapState = getState();
    const gapAreaTab = gapState.container?.activeTab;
    const gapArea = gapState.container?.areas?.[gapAreaTab];

    if (gapArea) {
      const coverageBoxes = getCoverageBoxes(gapState);
      const hasGap = calculateBarCoverageGap(gapArea, coverageBoxes, constants);
      // Show dialog only on first detection AND only when the user is currently on the
      // microphones menu. Without this guard the dialog can pop while the user is editing
      // speakers / cameras / floor plan, which reads as a context-jump bug. Use `currentView`
      // rather than `currentDevice` — the latter persists across non-device views.
      const inMicrophonesMenu = gapState.ui?.currentView === 'microphones';

      if (hasGap) {
        if (!gapArea.expansionMicRecommended) {
          dispatch(ReducersIndex.container.setExpansionMicRecommended(true as any));
        }
        // `setExpansionMicRecommended` above stays unconditional so the state stays truthful —
        // only the UI surface is gated to the relevant menu.
        if (gapArea.expansionMicType === null && inMicrophonesMenu) {
          dispatch(ReducersIndex.uiReducers.openModal('expansionMicRecommendation'));
        }
      } else {
        if (gapArea.expansionMicRecommended) {
          dispatch(ReducersIndex.container.setExpansionMicRecommended(false as any));
        }
      }
    }
  }

  // Open the expansion-mic dialog when the user switches INTO the microphones menu
  // and there's a pending recommendation that was suppressed earlier (e.g. dimensions
  // changed while the user was on the floor plan menu — gap detected, dialog skipped,
  // state set). This closes the loop: the user-visible prompt surfaces the moment the
  // user lands on the relevant menu.
  //
  // The persisted flag is NOT trusted on its own here. It is written by the detector above on a
  // `BAR_GAP_TRIGGERS` action, but several paths mutate the design afterwards without being
  // triggers themselves — `container/setPlatform` and `container/setProductFamily` are the ones
  // that matter in practice, and the Devio UCX capacity fallback (`applyUcxCapacityFallback` →
  // `promoteExpansionToPrimary`) reaches through them. That fallback removes the bar and nulls
  // `expansionMicType`, which used to satisfy this predicate on a flag describing a bar that no
  // longer exists. `promoteExpansionToPrimary` now clears the flag at the source; re-deriving the
  // gap here is the second half of that fix, and covers any future non-trigger path that leaves
  // the flag behind.
  //
  // Re-deriving is cheap (pure geometry over memoized coverage boxes, no DOM) and self-correcting:
  // `calculateBarCoverageGap` bails at its first guard when the primary mic isn't a bar, so a
  // barless design can't prompt regardless of what the flag says. When the two disagree the flag
  // is rewritten so the design state stays truthful, mirroring the detector's own contract above.
  if (localCalc && areaDefined && action.type === 'ui/setCurrentView' && action.payload === 'microphones') {
    const viewState = getState();
    const viewAreaTab = viewState.container?.activeTab;
    const viewArea = viewState.container?.areas?.[viewAreaTab];

    // Same geometry preconditions `getCoverageBoxes` dereferences unguarded (dimensions/location/
    // table); a partially-built area is left alone rather than throwing on a view change.
    if (viewArea?.dimensions && viewArea?.location && viewArea?.table) {
      const stillHasGap = calculateBarCoverageGap(viewArea, getCoverageBoxes(viewState), constants);

      if (!stillHasGap) {
        if (viewArea.expansionMicRecommended) {
          dispatch(ReducersIndex.container.setExpansionMicRecommended(false as any));
        }
      } else {
        if (!viewArea.expansionMicRecommended) {
          dispatch(ReducersIndex.container.setExpansionMicRecommended(true as any));
        }
        if (viewArea.expansionMicType === null) {
          dispatch(ReducersIndex.uiReducers.openModal('expansionMicRecommendation'));
        }
      }
    }
  }

  // Bar SPEAKER coverage gap detection (WDT-136). Mirrors the mic gap detection above —
  // when bar dimensions / acoustics / table / mic-model change, re-evaluate whether the
  // bar's built-in speakers leave a meaningful coverage gap. If so, set
  // `expansionSpeakerRecommended` unconditionally and open the dialog the moment the
  // user is on the speakers menu (gated on currentView to avoid context-jump pops).
  const BAR_SPEAKER_GAP_TRIGGERS = [
    'container/setMicrophones',
    'container/setAcoustics',
    'container/setDimensions',
    'container/setTable',
    'container/resetArea',
  ];
  if (localCalc && areaDefined && BAR_SPEAKER_GAP_TRIGGERS.some(t => action.type === t)) {
    const spkGapState = getState();
    const spkGapAreaTab = spkGapState.container?.activeTab;
    const spkGapArea = spkGapState.container?.areas?.[spkGapAreaTab];

    if (spkGapArea) {
      const coverageBoxes = getCoverageBoxes(spkGapState);
      const hasSpkGap = calculateBarSpeakerCoverageGap(spkGapArea, coverageBoxes, constants);

      // The speaker gap alone is sufficient to prompt — expansion speakers are
      // an independent opt-in from expansion mics. Declining (or never being offered)
      // expansion mics must not suppress the expansion speaker recommendation; a bar-only
      // design with a genuine speaker coverage gap still needs to hear about it.
      if (hasSpkGap) {
        if (!spkGapArea.expansionSpeakerRecommended) {
          dispatch(ReducersIndex.container.setExpansionSpeakerRecommended(true as any));
        }
        const inSpeakersMenu = spkGapState.ui?.currentView === 'speakers';
        // TC-05: Tesira has no bar + expansion equivalent, so once the expansion mics
        // already in place leave no room for even one expansion speaker (no free UCC
        // port AND no DCM-XA swap headroom), don't surface the recommendation at all —
        // it would only lead the user into the dialog to land on 0 speakers placed
        // (the same cap `setExpansionSpeakerLayout` enforces, reduxModules/ducks/room.ts).
        // The dialog always sets 'ceiling' on Add, so that's what the hypothetical checks.
        // With no expansion mics placed yet, this is trivially true (2 free ports).
        const speakerCap = toolIndex[spkGapState.ui?.tool]?.constants?.maxExpansionSpeakers ?? Infinity;
        const hasSpeakerPortRoom = maxExpansionSpeakerCountFor({ ...spkGapArea, expansionSpeakerType: 'ceiling' }, speakerCap) >= 1;
        if (spkGapArea.expansionSpeakerType === null && inSpeakersMenu && hasSpeakerPortRoom) {
          dispatch(ReducersIndex.uiReducers.openModal('expansionSpeakerRecommendation'));
        }
      } else if (spkGapArea.expansionSpeakerRecommended) {
        dispatch(ReducersIndex.container.setExpansionSpeakerRecommended(false as any));
      }
    }
  }

  // Speaker dialog view-switch trigger — symmetric to the mic one above. When the user
  // navigates to the speakers menu and there's a pending speaker recommendation, surface it.
  // Independent of expansion mics — see the gap-detection trigger above.
  if (action.type === 'ui/setCurrentView' && action.payload === 'speakers') {
    const viewState = getState();
    const viewAreaTab = viewState.container?.activeTab;
    const viewArea = viewState.container?.areas?.[viewAreaTab];
    // Same TC-05 port-budget gate as the gap-detection trigger above — don't resurrect
    // a pending recommendation on view-switch either if there's no room left for it.
    const speakerCap = toolIndex[viewState.ui?.tool]?.constants?.maxExpansionSpeakers ?? Infinity;
    const hasSpeakerPortRoom = viewArea
      ? maxExpansionSpeakerCountFor({ ...viewArea, expansionSpeakerType: 'ceiling' }, speakerCap) >= 1
      : false;
    if (viewArea?.expansionSpeakerRecommended && viewArea?.expansionSpeakerType === null && hasSpeakerPortRoom) {
      dispatch(ReducersIndex.uiReducers.openModal('expansionSpeakerRecommendation'));
    }
  }

  // the bar-speaker auto-coupling block that previously lived here is removed.
  // It silently set `expansionSpeakerType = 'ceiling'` whenever the user had expansion
  // mics + bar speakers, which fired on the recalc the mic dialog dispatches and caused
  // expansion speakers to appear "from the microphone dialog". Expansion speakers are
  // now strictly opt-in:
  //   1. `calculateBarSpeakerCoverageGap` (above) detects a bar-speaker coverage gap
  //   2. Sets `expansionSpeakerRecommended` and opens `expansionSpeakerRecommendation`
  //   3. User accepts → menu's expansion-speaker type / model dropdowns take over
  // Speaker placement no longer follows mic placement; the two flows are independent.
  // If the user changes away from bar speakers we leave the expansion-speaker state in
  // place — the menu condition (which requires a bar mic) hides the related UI, and the
  // user can clear it explicitly via the type dropdown.

  // Post-move proximity soft warning. When the user manually moves an expansion speaker,
  // `MuiDevices.onPointerUp` dispatches `updateDevice({ expansionSpeakers: ... })` without
  // the sync-middleware flag. Auto-placement already filters speakers landing within
  // `MIN_MIC_SPKR_DISTANCE_M` (0.86 m) of any mic, but manual moves bypass that filter
  // — the user owns the placement. Surface a warning so they can either accept the
  // feedback risk or nudge the speaker further.
  //
  // The check reports a count rather than singling out the just-moved speaker because
  // pre-existing violations from earlier moves should also surface (this is the only
  // post-move feedback point users see).
  if (action.type === 'container/updateDevice'
      && action.payload?.expansionSpeakers
      && !action.payload._fromSyncMiddleware) {
    const proxState = getState();
    const proxArea = proxState.container?.areas?.[proxState.container?.activeTab];
    const placedSpeakers = proxArea?.expansionSpeakers?.layout ?? [];
    if (placedSpeakers.length > 0) {
      // All mic positions that matter for feedback: bar mic + expansion mics (ceiling
      // or table — ttm_x at z=0.91 m still violates a 2D plan distance check).
      const nearbyMics = [
        ...(proxArea.microphones?.layout ?? []),
        ...(proxArea.expansionMicrophones?.layout ?? []),
      ];
      const violationCount = placedSpeakers.reduce((count: number, spkr: any) => {
        const violates = nearbyMics.some((mic: any) => {
          const dx = (spkr.location?.x ?? 0) - (mic.location?.x ?? 0);
          const dy = (spkr.location?.y ?? 0) - (mic.location?.y ?? 0);
          return Math.hypot(dx, dy) < MIN_MIC_SPKR_DISTANCE_M;
        });
        return violates ? count + 1 : count;
      }, 0);
      if (violationCount > 0) {
        showWarning(
          `${violationCount} expansion speaker(s) are within ${MIN_MIC_SPKR_DISTANCE_M} m of a microphone — risk of acoustic feedback.`,
          5000,
        );
      }
    }
  }

  return result;
};

export default calculateDevicesMiddleware;
